import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { gql, useApolloClient, useLazyQuery, useMutation, useQuery, useSubscription } from "@apollo/client";
import {
	Autocomplete,
	Card,
	Checkbox,
	Collapse,
	Dialog,
	DialogContent,
	Fade,
	Grid,
	Icon,
	InputAdornment,
	InputLabel,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Modal,
	Select,
	Switch,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	useMediaQuery,
} from "@mui/material";
import logo_alt from "assets/images/comedor_alt.png";
import { Chip } from "@mui/material";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import CompactPagination from "components/CompactPagination";
import CustomPagination from "components/Pagination";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import DataTableCustom from "components/Tables/DataTableCustom";
import TableV2 from "components/Tables/Table-v2";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { MenuContext } from "context/menuContext";
import { UserContext } from "context/user";
import dayjs from "dayjs";
import { createElement, useContext, useEffect, useMemo, useRef, useState } from "react";
import { formatDate, formatMoneyPunto } from "utils/formatters";
import { getUproColor } from "utils/colors";
import { SISTEMA_COLORS, SISTEMAS_NAME, TIPO_USER_ENUM } from "../menuUniversitario/ModalPrecioMenu/config";
import PropTypes from "prop-types";
import CategoriaVoucherSelectorDialog from "./components/CategoriaVoucherSelectorDialog";
import VoucherFisicoBuilder from "./components/VoucherFisicoBuilder";
import ProductoVoucherSelectorDialog from "./components/ProductoVoucherSelectorDialog";
import VoucherClienteDialog from "./components/VoucherClienteDialog";
import VoucherFacturacionTab from "./components/VoucherFacturacionTab";
import VouchersAsignadosDialog from "./components/VouchersAsignadosDialog";
import { buildVoucherUsageMetrics } from "./voucherUsageMetrics";
import {
	BENEFICIOS_VOUCHERS_PERMISSION_IDS,
	BENEFICIOS_VOUCHERS_PERMISSIONS,
} from "./permissions";

const VOUCHER_GRUPOS_QUERY = gql`
  query voucherGrupos {
    voucherGrupos {
      id
      modalidad
      nombre
      nroExpediente
      descripcion
      estado
      filePathOrdenProvision
      tipo
      total
      totalMonto
      totalUnidades
      consumido
      consumidoMonto
      consumidoUnidades
      cantidadVouchers
      cantidadClientes
      tieneUso
      tieneUsoActivo
      idSede
      sede { id nombre }
      idVoucherCliente
      clienteFacturacion { id tipoCliente nombre apellido razonSocial tipoDocumento numeroDocumento condicionIva cuit }
      desde
      hasta
    }
  }
`;

const VOUCHER_GRUPO_DETAIL_QUERY = gql`
  query voucherGrupo($idGrupo: ID!, $page: Int, $limit: Int, $search: String, $tipo: VoucherTipo, $habilitado: Boolean) {
    voucherGrupo(idGrupo: $idGrupo, page: $page, limit: $limit, search: $search, tipo: $tipo, habilitado: $habilitado) {
      id
      modalidad
      nombre
      nroExpediente
      descripcion
      estado
      filePathOrdenProvision
      tipo
      total
      totalMonto
      totalUnidades
      consumido
      consumidoMonto
      consumidoUnidades
      cantidadVouchers
      cantidadClientes
      tieneUso
      tieneUsoActivo
      idSede
      sede { id nombre }
      idVoucherCliente
      clienteFacturacion { id tipoCliente nombre apellido razonSocial tipoDocumento numeroDocumento condicionIva cuit }
      vouchersPagination {
        total
        pages
        page
        limit
      }
      vouchers {
        id
        idCliente
        desde
        hasta
        tipo
        idProducto
        idCategoria
        cantidad
        limiteDiario
        limitePedido
        habilitado
        consumido
        saldo
        tieneUso
        tieneUsoActivo
        cliente {
          id
          nombre
          apellido
          dni
          appUsuario
          tipoUsuario
        }
        categoria {
          id
          categoria
        }
        producto {
          id
          nombre
          disponibilidad
          categoriaProducto { id categoria }
        }
      }
    }
  }
`;

const VOUCHER_GRUPO_USO_LOG_QUERY = gql`
  query voucherGrupoUsoLog($idGrupo: ID!, $filter: VoucherGrupoUsoLogFilter, $page: Int, $limit: Int) {
    voucherGrupoUsoLog(idGrupo: $idGrupo, filter: $filter, page: $page, limit: $limit) {
      rows {
        idVenta idPedido fechaPedido estadoPedido totalPedido creado idVoucher nombreVoucher
		tipoVoucher cantidadUtilizada montoCubiertoVoucher remanenteCliente totalVenta
		voucherHabilitado productoVoucher categoriaVoucher
        cliente { id nombre apellido dni appUsuario tipoUsuario }
      }
      pagination { total pages page limit }
    }
  }
`;

const NUEVO_PEDIDO_SUBSCRIPTION = gql`
  subscription nuevoPedidoVoucherGrupoUsoLog {
    nuevoPedido {
      id
    }
  }
`;

const PEDIDO_MODIFICADO_SUBSCRIPTION = gql`
  subscription pedidoModificadoVoucherGrupoUsoLog {
    pedidoModificado {
      id
    }
  }
`;

const CATEGORIAS_QUERY = gql`
  query categoriasProductoVoucher {
    categoriasProducto(order: { field: "categoria", order: ASC }) {
      id
      categoria
    }
  }
`;

const SEDES_VOUCHER_GRUPO_QUERY = gql`
  query sedesVoucherGrupo { sedes { id nombre } }
`;

const VOUCHER_CLIENTES_GRUPO_QUERY = gql`
  query voucherClientesGrupo($page: Int, $limit: Int) {
    voucherClientes(page: $page, limit: $limit) {
      rows { id tipoCliente nombre apellido razonSocial tipoDocumento numeroDocumento condicionIva cuit }
    }
  }
`;

const CLIENTES_QUERY = gql`
  query clientesVoucher($filter: JSON, $search: String, $page: Int, $limit: Int) {
    paginationInfo {
      pages
      total
    }
    clientes(
      filter: $filter
      search: $search
      order: { field: "apellido", order: ASC }
      pagination: { page: $page, limit: $limit }
    ) {
      id
      nombre
      apellido
      dni
      appUsuario
      tipoUsuario
    }
  }
`;

const CREAR_GRUPO_CON_VOUCHERS = gql`
  mutation crearGrupoConVouchers($input: CrearGrupoConVouchersInput!, $ordenProvisionFile: Upload) {
    crearGrupoConVouchers(input: $input, ordenProvisionFile: $ordenProvisionFile) {
      id
      estado
      filePathOrdenProvision
    }
  }
`;

const ACTUALIZAR_ESTADO_GRUPO = gql`
  mutation actualizarEstadoVoucherGrupo($idGrupo: ID!, $estado: VoucherGrupoEstado!) {
    actualizarEstadoVoucherGrupo(idGrupo: $idGrupo, estado: $estado) {
      id
      estado
    }
  }
`;

const ELIMINAR_VOUCHER_GRUPO = gql`
  mutation eliminarVoucherGrupo($idGrupo: ID!, $confirmacionNombre: String!, $deleteMotivo: String!) {
    eliminarVoucherGrupo(idGrupo: $idGrupo, confirmacionNombre: $confirmacionNombre, deleteMotivo: $deleteMotivo)
  }
`;

const ELIMINAR_VOUCHER = gql`
  mutation eliminarVoucher($idVoucher: ID!, $confirmacionDni: String!) {
    eliminarVoucher(idVoucher: $idVoucher, confirmacionDni: $confirmacionDni)
  }
`;

const ELIMINAR_VOUCHERS = gql`
  mutation eliminarVouchers($idVouchers: [ID!]!, $confirmacionNombreGrupo: String!) {
    eliminarVouchers(idVouchers: $idVouchers, confirmacionNombreGrupo: $confirmacionNombreGrupo)
  }
`;

const DEFAULT_GROUP_FORM = {
	id: null,
	modalidad: "PERSONAL",
	nombre: "",
	descripcion: "",
	nroExpediente: "",
	estado: "Borrador",
	ordenProvisionFile: null,
	existingOrdenProvisionPath: "",
	tieneUso: false,
	tieneUsoActivo: false,
	idSede: "",
	idVoucherCliente: "",
	clienteFacturacion: null,
};

const getTodayVoucherDate = () => dayjs().startOf("day").format("YYYY-MM-DD");
const getVoucherHastaFromDesde = (desde, amount = 1, unit = "month") =>
	dayjs(desde).add(amount, unit).format("YYYY-MM-DD");
const getVoucherDurationDays = (desde, hasta) => {
	if (!desde || !hasta || !dayjs(desde).isValid() || !dayjs(hasta).isValid()) return null;
	const diff = dayjs(hasta).diff(dayjs(desde), "day");
	return diff >= 0 ? diff : null;
};

const DEFAULT_VOUCHER_FORM = {
	desde: "",
	hasta: "",
	tipo: "MONTO",
	idProducto: "",
	idCategoria: "",
	producto: null,
	categoria: null,
	cantidad: null,
	limiteDiario: null,
};

const createDefaultVoucherForm = () => {
	const desde = getTodayVoucherDate();
	return {
		...DEFAULT_VOUCHER_FORM,
		desde,
		hasta: getVoucherHastaFromDesde(desde),
	};
};

const DEFAULT_CLIENT_FILTERS = {
	nombre: "",
	apellido: "",
	dni: "",
	appUsuario: "",
	tipoUsuario: "",
};

const DEFAULT_TRANSFER_FILTERS = {
	available: "",
	selected: "",
};

const AVAILABLE_CLIENTES_PAGE_SIZE = 100;

const APP_USUARIO_FILTER_OPTIONS = [
	{ value: "compras", label: "SGU" },
	{ value: "Comedor", label: "Comedor" },
	{ value: "Alumnos", label: "Academica" },
];

const TIPO_USUARIO_FILTER_OPTIONS = {
	compras: [{ value: "Empleado", label: "Empleado" }],
	Comedor: [
		{ value: "DOCENTE", label: "Docente" },
		{ value: "ALUMNO", label: "Alumno" },
		{ value: "PERSONALAPOYO", label: "Personal de apoyo" },
		{ value: "Externo", label: "Externo" },
		{ value: "Empleado", label: "Empleado" },
	],
	Alumnos: [
		{ value: "Alumno", label: "Alumno" },
		{ value: "Gestion Academica", label: "Gestion Academica" },
		{ value: "Docente", label: "Docente" },
	],
};

const GROUP_STATUS_OPTIONS = ["Borrador", "Activo", "Inactivo"];
const GROUP_STATUS_DOT_COLORS = {
	Activo: "#48c7b5",
	Inactivo: "#ef4444",
	Borrador: "#f59e0b",
};
const GROUP_FIELD_LABEL_SX = {
	mb: 0.75,
	color: "#253451",
	fontSize: "0.8rem",
	fontWeight: 700,
	lineHeight: 1.25,
};
const GROUP_SOFT_INPUT_SX = {
	"& > div": {
		width: "100%",
		borderRadius: "10px",
		boxShadow: "0 4px 12px rgba(37, 52, 81, 0.08)",
		transition: "border-color 200ms ease, box-shadow 200ms ease",
	},
	"& > div:focus-within": {
		boxShadow: "0 0 0 3px rgba(52, 99, 206, 0.12), 0 4px 12px rgba(37, 52, 81, 0.08)",
	},
	"& .MuiInputBase-root": {
		width: "auto !important",
		minWidth: 0,
		flex: "1 1 auto",
	},
	"& .MuiInputBase-input": {
		width: "100% !important",
		minWidth: 0,
		color: "#344563",
		fontSize: "0.875rem !important",
		fontWeight: 500,
		lineHeight: "1.4 !important",
		textOverflow: "clip",
	},
	"& .MuiInputBase-input::placeholder": { color: "#8290a8 !important", opacity: "1 !important" },
	"& .material-icons": { color: "#8295b7", fontSize: "1.1rem !important" },
};
const GROUP_SELECT_SX = {
	backgroundColor: "#fff",
	borderRadius: "10px",
	boxShadow: "0 4px 12px rgba(37, 52, 81, 0.08)",
	color: "#344563",
	fontSize: "0.875rem",
	fontWeight: 500,
	transition: "border-color 200ms ease, box-shadow 200ms ease",
	"& .MuiOutlinedInput-notchedOutline": { borderColor: "#d5deea" },
	"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#aebdd1" },
	"&.Mui-focused": { boxShadow: "0 0 0 3px rgba(52, 99, 206, 0.12), 0 4px 12px rgba(37, 52, 81, 0.08)" },
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3463ce", borderWidth: 1 },
	"&.Mui-disabled": { backgroundColor: "#f1f5f9", boxShadow: "none" },
	"& .MuiSelect-select": { display: "flex", alignItems: "center", minWidth: 0, py: 0 },
	"& .MuiSelect-icon": { right: 14, color: "#253451" },
};
const GROUP_INPUT_ADORNMENT_SX = {
	mr: 0.75,
	color: "#8295b7",
	"& .MuiIcon-root": { fontSize: "1.3rem" },
};
const GROUP_HELPER_TEXT_SX = {
	mt: 0.75,
	color: "#8290a8",
	fontSize: "0.72rem",
	lineHeight: 1.35,
};
const DEFAULT_GROUP_TABLE_FILTERS = {
	modalidad: "",
	estado: "",
	tipo: "",
	nroExpediente: "",
	idGrupo: null,
	desde: "",
	hasta: "",
};
const GROUP_TABLE_COLUMN_OPTIONS = [
	{ field: "nombre", label: "Grupo" },
	{ field: "expediente", label: "Expediente" },
	{ field: "estado", label: "Estado" },
	{ field: "tipo", label: "Tipo" },
	{ field: "sede", label: "Sede" },
	{ field: "clienteFacturacion", label: "Cliente facturable" },
	{ field: "desde", label: "Desde" },
	{ field: "hasta", label: "Hasta" },
	{ field: "clientes", label: "Cupones" },
	{ field: "total", label: "Total" },
	{ field: "consumido", label: "Consumido" },
	{ field: "adjuntos", label: "Adjuntos" },
];
const DEFAULT_VISIBLE_GROUP_COLUMN_FIELDS = GROUP_TABLE_COLUMN_OPTIONS.map((option) => option.field);
const COMPACT_VISIBLE_GROUP_COLUMN_FIELDS = ["nombre"];
const GROUP_TABLE_COLUMN_PRIORITY = [
	["nombre"],
	["expediente"],
	["estado"],
	["tipo"],
	["clienteFacturacion"],
	["total"],
	["consumido"],
	["desde", "hasta"],
	["clientes"],
	["sede"],
	["adjuntos"],
];
const GROUP_TABLE_RESPONSIVE_WIDTH_BUFFER = 24;
const DEFAULT_VOUCHER_TABLE_FILTERS = {
	search: "",
	tipo: "",
	habilitado: "",
};
const DEFAULT_VOUCHER_TABLE_PAGINATION = {
	total: 0,
	pages: 0,
	page: 1,
	limit: 10,
};
const DEFAULT_VOUCHER_LOG_FILTERS = {
	search: "",
	estadoPedido: "",
	appUsuario: "",
	tipoUsuario: "",
	tipoVoucher: "",
	fechaDesde: "",
	fechaHasta: "",
};
const VOUCHER_LOG_PAGE_SIZE = 10;
const VOUCHER_LOG_METRICS_PAGE_SIZE = 100;

const GROUP_TABLE_CELL_HORIZONTAL_PADDING = 32;
const GROUP_TABLE_TEXT_MEASUREMENT_BUFFER = 12;
const GROUP_TABLE_SORT_ICON_WIDTH = 22;
const GROUP_TABLE_STATUS_BADGE_EXTRA_WIDTH = 28;
const GROUP_TABLE_ACTION_ICON_WIDTH = 24;
const ACCESSIBLE_GREEN = "#147877";
let groupTableMeasureContext = null;

const measureGroupTableText = (value, font) => {
	const text = String(value ?? "");
	if (typeof document === "undefined") return text.length * 7;
	if (!groupTableMeasureContext) {
		groupTableMeasureContext = document.createElement("canvas").getContext("2d");
	}
	if (!groupTableMeasureContext) return text.length * 7;
	groupTableMeasureContext.font = font;
	return groupTableMeasureContext.measureText(text).width;
};

const getAdjuntoUrl = (pathValue) => {
	if (!pathValue) return "";
	if (/^https?:\/\//i.test(pathValue)) return pathValue;
	return resolveStoredFileUrl(String(pathValue).replace(/^\/+/, ""));
};

const getGroupStatusBadgeProps = (estado) => {
	if (estado === "Activo") return { color: "success", label: "Activo" };
	if (estado === "Inactivo") return { color: "error", label: "Inactivo" };
	return { color: "warning", label: "Borrador" };
};

const getPedidoStatusBadgeProps = (pedido) => {
	const estado = pedido?.estadoPedido || "-";
	const reservaVencida = estado === "RESERVADO"
		&& pedido?.fechaPedido
		&& dayjs(pedido.fechaPedido).isBefore(dayjs().startOf("day"));

	const label = reservaVencida
		? "Reserva vencida"
		: estado === "RESERVADO" && pedido?.fechaPedido
			? `Reservado para ${formatDate(pedido.fechaPedido, "XXXX")}`
			: estado;

	const color = estado === "ABIERTO"
		? getUproColor("primary")
		: estado === "CERRADO"
			? getUproColor("uproGreen")
			: estado === "ANULADO" || estado === "PAGO EN REVISIÓN"
				? getUproColor("uproBlue")
				: getUproColor("uproYellow");

	return { color, label };
};


const sortClientes = (clientes) =>
	[...clientes].sort((a, b) =>
		`${a?.apellido || ""} ${a?.nombre || ""}`.localeCompare(
			`${b?.apellido || ""} ${b?.nombre || ""}`,
			"es"
		)
	);

const buildLikeFilter = (value) => ({ _like: `%${value.trim()}%` });
const getClienteNombreCompleto = (cliente) => `${cliente?.apellido || "-"}, ${cliente?.nombre || "-"}`;
const getVoucherDeleteClientLabel = (cliente) => `${cliente?.nombre || "-"} ${cliente?.apellido || "-"} - DNI ${cliente?.dni || "-"}`;
const formatDateCell = (value) => {
	if (!value || !dayjs(value).isValid()) return "-";
	const date = dayjs(value);
	return date.year() === dayjs().year() ? date.format("DD/MM") : date.format("DD/MM/YYYY");
};
const formatGroupTableDateCell = (value) => {
	if (!value || !dayjs(value).isValid()) return "-";
	const date = dayjs(value);
	return date.year() === dayjs().year() ? date.format("DD/MM") : date.format("DD/MM/YYYY");
};
const formatVoucherType = (value, fallback = "-") => {
	if (value === "MONTO") return "Monto";
	if (value === "PRODUCTO") return "Producto";
	if (value === "CATEGORIA") return "Categoría";
	return fallback;
};
const formatVoucherValue = (value, tipo) => tipo === "MONTO" ? `$ ${formatMoneyPunto(Number(value || 0))}` : Number(value || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const formatVoucherMoneyCompact = (value) => `$${formatMoneyPunto(Number(value || 0))}`;
const formatVoucherUnitsCompact = (value) => Number(value || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const formatVoucherPercentage = (consumed, assigned) => {
	const assignedValue = Number(assigned || 0);
	if (assignedValue <= 0) return "0 %";
	return `${((Number(consumed || 0) / assignedValue) * 100).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`;
};
const formatVoucherTotalValue = (group, showBreakdown = false) => {
	const normalizedTipo = String(group?.tipo || "").trim().toUpperCase();
	const hasMixedTypes = !normalizedTipo && Number(group?.cantidadVouchers || 0) > 0;

	if (normalizedTipo === "MONTO") {
		return showBreakdown
			? `${formatVoucherMoneyCompact(group?.total)} / ${formatVoucherUnitsCompact(0)}`
			: formatVoucherValue(group?.total, normalizedTipo);
	}
	if (["PRODUCTO", "CATEGORIA"].includes(normalizedTipo)) {
		return showBreakdown
			? `${formatVoucherMoneyCompact(0)} / ${formatVoucherUnitsCompact(group?.total)}`
			: formatVoucherValue(group?.total, normalizedTipo);
	}
	if (hasMixedTypes || showBreakdown) {
		return `${formatVoucherMoneyCompact(group?.totalMonto || 0)} / ${formatVoucherUnitsCompact(group?.totalUnidades || 0)}`;
	}

	return formatVoucherUnitsCompact(group?.total);
};
const formatVoucherConsumedValue = (group, showBreakdown = false) => {
	const normalizedTipo = String(group?.tipo || "").trim().toUpperCase();
	const hasMixedTypes = !normalizedTipo && Number(group?.cantidadVouchers || 0) > 0;

	if (normalizedTipo === "MONTO") {
		return showBreakdown
			? `${formatVoucherMoneyCompact(group?.consumido)} / ${formatVoucherUnitsCompact(0)}`
			: formatVoucherMoneyCompact(group?.consumido);
	}
	if (["PRODUCTO", "CATEGORIA"].includes(normalizedTipo)) {
		return showBreakdown
			? `${formatVoucherMoneyCompact(0)} / ${formatVoucherUnitsCompact(group?.consumido)}`
			: formatVoucherUnitsCompact(group?.consumido);
	}
	if (hasMixedTypes || showBreakdown) {
		return `${formatVoucherMoneyCompact(group?.consumidoMonto || 0)} / ${formatVoucherUnitsCompact(group?.consumidoUnidades || 0)}`;
	}

	return formatVoucherUnitsCompact(group?.consumido);
};
const getPreviewMode = (file) => !file ? null : String(file.type || "").startsWith("image/") || /\.(png|jpg|jpeg)$/i.test(String(file.name || "")) ? "image" : String(file.type || "") === "application/pdf" || /\.pdf$/i.test(String(file.name || "")) ? "pdf" : "file";
const formatFileSize = (size) => !size ? "0 KB" : size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KB` : `${(size / (1024 * 1024)).toFixed(1)} MB`;
const matchesClienteFilter = (cliente, value) => !value.trim() || `${cliente?.nombre || ""} ${cliente?.apellido || ""} ${cliente?.dni || ""}`.toLowerCase().includes(value.trim().toLowerCase());
const buildClientesFilter = (filters) => {
	const filter = {};
	if (filters.nombre.trim()) filter.nombre = buildLikeFilter(filters.nombre);
	if (filters.apellido.trim()) filter.apellido = buildLikeFilter(filters.apellido);
	if (filters.dni.trim()) filter.dni = buildLikeFilter(filters.dni);
	if (filters.appUsuario.trim()) filter.appUsuario = filters.appUsuario.trim();
	if (filters.tipoUsuario.trim()) filter.tipoUsuario = filters.tipoUsuario.trim();
	return filter;
};

const normalizeEnumKey = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/\s+/g, "")
		.toLowerCase();

const TIPO_USER_LABELS = Object.entries(TIPO_USER_ENUM).reduce((acc, [key, label]) => {
	acc[normalizeEnumKey(key)] = label;
	return acc;
}, {});

const getSistemaLabel = (appUsuario) => SISTEMAS_NAME[appUsuario] || appUsuario || "Particular";
const getSistemaColor = (appUsuario) => SISTEMA_COLORS[appUsuario] || null;
const getTipoUsuarioLabel = (tipoUsuario) => TIPO_USER_LABELS[normalizeEnumKey(tipoUsuario)] || tipoUsuario || "Sin tipo";

const renderClienteIdentityChips = (cliente, { showDni = false, sx = {} } = {}) => {
	const sistemaColor = getSistemaColor(cliente?.appUsuario);

	return (
		<SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={0.75} sx={sx}>
			{showDni && cliente?.dni ? (
				<Chip
					label={`DNI ${cliente.dni}`}
					size="small"
					variant="outlined"
					sx={{
						height: 24,
						borderColor: "rgba(148,163,184,.45)",
						color: "#64748b",
						backgroundColor: "#fff",
					}}
				/>
			) : null}
			<Chip
				label={getSistemaLabel(cliente?.appUsuario)}
				size="small"
				sx={{
					height: 24,
					backgroundColor: sistemaColor ? getUproColor(sistemaColor) : "#e2e8f0",
					color: sistemaColor ? "#fff" : "#475569",
					"& .MuiChip-label": { px: 1 },
				}}
			/>
			{cliente?.tipoUsuario ? (
				<Chip
					label={getTipoUsuarioLabel(cliente.tipoUsuario)}
					size="small"
					variant="outlined"
					sx={{
						height: 24,
						borderColor: "rgba(148,163,184,.45)",
						color: "#64748b",
						backgroundColor: "#fff",
						"& .MuiChip-label": { px: 1 },
					}}
				/>
			) : null}
		</SoftBox>
	);
};

const getVoucherBadgeProps = (voucher) => {
	if (voucher?.habilitado === false) return { label: "Deshabilitado", color: "error" };
	if (!voucher?.id || voucher?.estadoLocal === "Pendiente") return { label: "Pendiente", color: "warning" };
	return { label: "Activo", color: "success" };
};

const renderClienteOption = (cliente) => {
	return (
		<SoftBox py={0.55}>
			<SoftTypography variant="button" fontWeight="medium" color="dark" sx={{ lineHeight: 1.25 }}>
				{getClienteNombreCompleto(cliente)}
			</SoftTypography>
			{renderClienteIdentityChips(cliente, { showDni: true, sx: { mt: 0.75 } })}
		</SoftBox>
	);
};

const mapPersistedVoucherToStage = (voucher) => ({
	tempId: `persisted-${voucher.id}`,
	id: Number(voucher.id),
	idCliente: Number(voucher.idCliente || voucher?.cliente?.id || 0) || null,
	cliente: voucher.cliente,
	desde: voucher.desde,
	hasta: voucher.hasta,
	tipo: voucher.tipo,
	idProducto: Number(voucher.idProducto || 0) || null,
	idCategoria: Number(voucher.idCategoria || 0) || null,
	producto: voucher.producto || null,
	categoria: voucher.categoria || null,
	cantidad: Number(voucher.cantidad || 0),
	limiteDiario: voucher.limiteDiario == null ? null : Number(voucher.limiteDiario),
	limitePedido: voucher.limitePedido == null ? null : Number(voucher.limitePedido),
	habilitado: voucher.habilitado !== false,
	tieneUso: Boolean(voucher.tieneUso),
	tieneUsoActivo: Boolean(voucher.tieneUsoActivo),
	estadoLocal: "Guardado",
});

const getVoucherConfigurationKey = (voucher = {}) => JSON.stringify({
	tipo: voucher.tipo || "",
	idProducto: voucher.tipo === "PRODUCTO" ? Number(voucher.idProducto || 0) : null,
	idCategoria: voucher.tipo === "CATEGORIA" ? Number(voucher.idCategoria || 0) : null,
	cantidad: Number(voucher.cantidad || 0),
	limiteDiario: Number(voucher.limiteDiario || 0),
	limitePedido: voucher.limitePedido == null ? null : Number(voucher.limitePedido),
	desde: String(voucher.desde || ""),
	hasta: String(voucher.hasta || ""),
});

const formatVoucherConfigurationLabel = (voucher = {}) => {
	const tipo = formatVoucherType(voucher.tipo);
	const destino = voucher.tipo === "PRODUCTO"
		? `, Producto: ${voucher?.producto?.nombre || "-"}`
		: voucher.tipo === "CATEGORIA"
			? `, Categoría: ${voucher?.categoria?.categoria || "-"}`
			: "";
	const cantidad = voucher.tipo === "MONTO"
		? formatVoucherValue(voucher.cantidad, voucher.tipo)
		: formatVoucherUnitsCompact(voucher.cantidad);
	const limiteDiario = voucher.tipo === "MONTO"
		? formatVoucherValue(voucher.limiteDiario, voucher.tipo)
		: formatVoucherUnitsCompact(voucher.limiteDiario);

	return `Tipo: ${tipo}${destino}, Cantidad: ${cantidad}, Límite diario: ${voucher.limiteDiario == null ? "Sin tope" : limiteDiario}${voucher.limitePedido != null ? `, Por papel: ${formatVoucherValue(voucher.limitePedido, voucher.tipo)}` : ""}, del ${formatDateCell(voucher.desde)} al ${formatDateCell(voucher.hasta)}`;
};

const buildVoucherEditorFingerprint = (form = {}, clientes = []) => JSON.stringify({
	desde: form.desde || "",
	hasta: form.hasta || "",
	tipo: form.tipo || "",
	idProducto: Number(form.idProducto || 0) || null,
	idCategoria: Number(form.idCategoria || 0) || null,
	cantidad: Number(form.cantidad || 0),
	limiteDiario: Number(form.limiteDiario || 0),
	clientes: [...new Set((clientes || []).map((cliente) => Number(cliente?.id || 0)).filter(Boolean))].sort((a, b) => a - b),
});

const buildGroupDraftFingerprint = (form = {}, vouchers = []) => JSON.stringify({
	form: {
		id: Number(form.id || 0) || null,
		modalidad: form.modalidad || "PERSONAL",
		nombre: form.nombre || "",
		descripcion: form.descripcion || "",
		nroExpediente: form.nroExpediente || "",
		estado: form.estado || "Borrador",
		idSede: Number(form.idSede || 0) || null,
		idVoucherCliente: Number(form.idVoucherCliente || 0) || null,
		existingOrdenProvisionPath: form.existingOrdenProvisionPath || "",
		ordenProvisionFile: form.ordenProvisionFile
			? `${form.ordenProvisionFile.name || ""}:${form.ordenProvisionFile.size || 0}:${form.ordenProvisionFile.lastModified || 0}`
			: null,
	},
	vouchers: (vouchers || []).map((voucher) => ({
		tempId: voucher.tempId,
		id: Number(voucher.id || 0) || null,
		idCliente: Number(voucher.idCliente || 0),
		...JSON.parse(getVoucherConfigurationKey(voucher)),
		habilitado: voucher.habilitado !== false,
	})).sort((left, right) => String(left.tempId).localeCompare(String(right.tempId))),
});

const UNSAVED_DIALOG_ACTION_SX = {
	minHeight: 46,
	borderRadius: "8px",
	fontSize: { xs: "0.7rem", sm: "0.8rem" },
	fontWeight: 700,
	letterSpacing: "0.01em",
	textTransform: "uppercase",
	transition: "background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease",
	"&:focus-visible": {
		outline: "3px solid rgba(47, 114, 223, 0.28)",
		outlineOffset: "2px",
	},
};

function UnsavedChangesDialog({
	open,
	onClose,
	description,
	dialogId,
	onDiscard,
	onSave,
	saveDisabled = false,
}) {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			aria-labelledby={`${dialogId}-title`}
			aria-describedby={`${dialogId}-description`}
			PaperProps={{
				sx: {
					width: "calc(100% - 24px)",
					maxWidth: 460,
					m: 1.5,
					borderRadius: "16px",
					boxShadow: "0 20px 48px rgba(15, 23, 42, 0.24)",
					overflow: "hidden",
				},
			}}
		>
			<DialogContent sx={{ px: { xs: 2.5, sm: 3.25 }, py: { xs: 2.75, sm: 3.25 } }}>
				<SoftBox display="flex" justifyContent="center" alignItems="center" gap={{ xs: 1.25, sm: 1.5 }}>
					<SoftBox
						display="flex"
						alignItems="center"
						justifyContent="center"
						width={{ xs: 40, sm: 44 }}
						height={{ xs: 40, sm: 44 }}
						minWidth={{ xs: 40, sm: 44 }}
						borderRadius="50%"
						sx={{ backgroundColor: "rgba(245, 155, 36, 0.12)" }}
					>
						<Icon
							aria-hidden="true"
							sx={{ color: "#F59B24", fontSize: { xs: "24px !important", sm: "27px !important" } }}
						>
							error_outline
						</Icon>
					</SoftBox>
					<SoftTypography
						id={`${dialogId}-title`}
						variant="h4"
						fontWeight="bold"
						sx={{ color: "#172033", fontSize: { xs: "1.15rem", sm: "1.45rem" }, lineHeight: 1.2 }}
					>
						Cambios sin guardar
					</SoftTypography>
				</SoftBox>

				<SoftTypography
					id={`${dialogId}-description`}
					variant="body2"
					display="block"
					mt={{ xs: 1.75, sm: 2.25 }}
					mx="auto"
					sx={{
						maxWidth: 380,
						color: "#5F6B85",
						fontSize: { xs: "0.86rem", sm: "0.94rem" },
						lineHeight: 1.5,
						textAlign: "center",
					}}
				>
					{description}
				</SoftTypography>

				<SoftBox mt={{ xs: 2.25, sm: 2.5 }} display="flex" flexDirection="column" gap={{ xs: 1, sm: 1.15 }}>
					{onSave ? (
						<SoftButton
							color="primary"
							fullWidth
							disabled={saveDisabled}
							onClick={onSave}
							sx={{
								...UNSAVED_DIALOG_ACTION_SX,
								background: "linear-gradient(135deg, #3478E5 0%, #2864CF 100%)",
								boxShadow: "0 8px 18px rgba(40, 100, 207, 0.24)",
								"&:hover": {
									background: "linear-gradient(135deg, #2F70D8 0%, #245BC0 100%)",
									boxShadow: "0 10px 22px rgba(40, 100, 207, 0.3)",
								},
								"&:disabled": {
									background: "linear-gradient(135deg, #3478E5 0%, #2864CF 100%)",
									color: "#FFFFFF",
									opacity: 0.5,
								},
							}}
						>
							Guardar cambios y salir
						</SoftButton>
					) : null}

					<SoftButton
						color="primary"
						variant="outlined"
						fullWidth
						onClick={onClose}
						sx={{
							...UNSAVED_DIALOG_ACTION_SX,
							background: "#FFFFFF",
							border: "1.5px solid #3478E5",
							color: "#2864CF",
							"&:hover": {
								background: "rgba(52, 120, 229, 0.06)",
								borderColor: "#2864CF",
							},
						}}
					>
						Continuar editando
					</SoftButton>

					<SoftButton
						color="light"
						fullWidth
						onClick={onDiscard}
						sx={{
							...UNSAVED_DIALOG_ACTION_SX,
							background: "#F4F5F7",
							boxShadow: "none",
							color: "#4A556D",
							"&:hover": {
								background: "#E9ECF1",
								boxShadow: "none",
							},
						}}
					>
						Salir sin guardar
					</SoftButton>
				</SoftBox>
			</DialogContent>
		</Dialog>
	);
}

UnsavedChangesDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	description: PropTypes.node.isRequired,
	dialogId: PropTypes.string.isRequired,
	onDiscard: PropTypes.func.isRequired,
	onSave: PropTypes.func,
	saveDisabled: PropTypes.bool,
};

function VoucherBuilderModal({ open, onClose, onSubmit, categorias, editingConfiguration }) {
	const { handleSnackbar } = useContext(MessageManager);
	const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("lg"));
	const lastAvailableSearchRef = useRef("");
	const sourceVouchers = useMemo(() => editingConfiguration?.vouchers || [], [editingConfiguration]);
	const initialVoucher = sourceVouchers[0] || null;
	const isEditingVoucher = Boolean(initialVoucher);
	const [form, setForm] = useState(createDefaultVoucherForm());
	const [clientesFilters, setClientesFilters] = useState(DEFAULT_CLIENT_FILTERS);
	const [transferFilters, setTransferFilters] = useState(DEFAULT_TRANSFER_FILTERS);
	const [clientesPage, setClientesPage] = useState(1);
	const [clientesResultados, setClientesResultados] = useState([]);
	const [appliedClientesFilters, setAppliedClientesFilters] = useState(DEFAULT_CLIENT_FILTERS);
	const [manualSelectedClientes, setManualSelectedClientes] = useState([]);
	const [availableCheckedClientes, setAvailableCheckedClientes] = useState({});
	const [selectedCheckedIds, setSelectedCheckedIds] = useState([]);
	const [searchExecuted, setSearchExecuted] = useState(false);
	const [targetSelector, setTargetSelector] = useState({ open: false, tipo: null });
	const [editorBaseline, setEditorBaseline] = useState("");
	const [discardEditorDialogOpen, setDiscardEditorDialogOpen] = useState(false);
	const [buscarClientes, { loading: loadingClientes }] = useLazyQuery(CLIENTES_QUERY, { fetchPolicy: "network-only" });

	const clientesBuscados = useMemo(() => sortClientes(clientesResultados), [clientesResultados]);
	const selectedClientes = useMemo(() => sortClientes(manualSelectedClientes), [manualSelectedClientes]);
	const selectedIds = useMemo(() => selectedClientes.map((cliente) => Number(cliente.id)), [selectedClientes]);
	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
	const availableCheckedSet = useMemo(() => new Set(Object.keys(availableCheckedClientes).map((id) => Number(id))), [availableCheckedClientes]);
	const selectedCheckedSet = useMemo(() => new Set(selectedCheckedIds.map((id) => Number(id))), [selectedCheckedIds]);
	const availableClientes = useMemo(() => clientesBuscados.filter((cliente) => !selectedSet.has(Number(cliente.id))), [clientesBuscados, selectedSet]);
	const clientesPagesInfo = useMemo(() => ({
		total: availableClientes.length,
		pages: availableClientes.length === 0 ? 0 : Math.ceil(availableClientes.length / AVAILABLE_CLIENTES_PAGE_SIZE),
	}), [availableClientes]);
	const normalizedClientesPage = useMemo(() => {
		if (clientesPagesInfo.pages === 0) return 1;
		return Math.min(clientesPage, clientesPagesInfo.pages);
	}, [clientesPage, clientesPagesInfo.pages]);
	const availablePageClientes = useMemo(() => {
		const start = (normalizedClientesPage - 1) * AVAILABLE_CLIENTES_PAGE_SIZE;
		return availableClientes.slice(start, start + AVAILABLE_CLIENTES_PAGE_SIZE);
	}, [availableClientes, normalizedClientesPage]);
	const selectedFiltered = useMemo(() => sortClientes(selectedClientes).filter((cliente) => matchesClienteFilter(cliente, transferFilters.selected)), [selectedClientes, transferFilters.selected]);
	const availableVisibleIds = useMemo(() => availableClientes.map((cliente) => Number(cliente.id)), [availableClientes]);
	const selectedVisibleIds = useMemo(() => selectedFiltered.map((cliente) => Number(cliente.id)), [selectedFiltered]);
	const lockedClienteIdSet = useMemo(
		() => new Set(sourceVouchers.map((voucher) => Number(voucher.idCliente || voucher?.cliente?.id || 0)).filter(Boolean)),
		[sourceVouchers]
	);
	const movableSelectedVisibleIds = useMemo(
		() => selectedVisibleIds.filter((id) => !lockedClienteIdSet.has(id)),
		[lockedClienteIdSet, selectedVisibleIds]
	);
	const availableAllChecked = useMemo(() => availableVisibleIds.length > 0 && availableVisibleIds.every((id) => availableCheckedSet.has(id)), [availableCheckedSet, availableVisibleIds]);
	const availableIndeterminate = useMemo(() => availableVisibleIds.some((id) => availableCheckedSet.has(id)) && !availableAllChecked, [availableAllChecked, availableCheckedSet, availableVisibleIds]);
	const selectedAllChecked = useMemo(() => movableSelectedVisibleIds.length > 0 && movableSelectedVisibleIds.every((id) => selectedCheckedSet.has(id)), [movableSelectedVisibleIds, selectedCheckedSet]);
	const selectedIndeterminate = useMemo(() => movableSelectedVisibleIds.some((id) => selectedCheckedSet.has(id)) && !selectedAllChecked, [movableSelectedVisibleIds, selectedAllChecked, selectedCheckedSet]);
	const todayDate = useMemo(() => getTodayVoucherDate(), []);
	const cantidadDias = useMemo(() => getVoucherDurationDays(form.desde, form.hasta), [form.desde, form.hasta]);
	const editorFingerprint = useMemo(() => buildVoucherEditorFingerprint(form, selectedClientes), [form, selectedClientes]);
	const hasUnsavedEditorChanges = Boolean(open && editorBaseline && editorFingerprint !== editorBaseline);
	const validForm = useMemo(() => {
		const keepsExistingPastStart = isEditingVoucher && String(form.desde || "") === String(initialVoucher?.desde || "");
		return form.desde
			&& form.hasta
			&& (keepsExistingPastStart || !dayjs(form.desde).isBefore(dayjs(todayDate), "day"))
			&& !dayjs(form.hasta).isBefore(dayjs(form.desde), "day")
			&& form.tipo
			&& (form.tipo !== "PRODUCTO" || form.idProducto)
			&& (form.tipo !== "CATEGORIA" || form.idCategoria)
			&& Number(form.cantidad) > 0
			&& Number(form.limiteDiario) > 0;
	}, [form, initialVoucher, isEditingVoucher, todayDate]);

	useEffect(() => {
		if (!open) return;
		setClientesFilters(DEFAULT_CLIENT_FILTERS);
		setTransferFilters(DEFAULT_TRANSFER_FILTERS);
		setClientesPage(1);
		setClientesResultados([]);
		setAppliedClientesFilters(DEFAULT_CLIENT_FILTERS);
		setAvailableCheckedClientes({});
		setSelectedCheckedIds([]);
		setSearchExecuted(false);
		setTargetSelector({ open: false, tipo: null });
		setDiscardEditorDialogOpen(false);
		lastAvailableSearchRef.current = "";

		if (initialVoucher) {
			const nextForm = {
				desde: initialVoucher.desde || "",
				hasta: initialVoucher.hasta || "",
				tipo: initialVoucher.tipo || "MONTO",
				idProducto: initialVoucher.idProducto ? String(initialVoucher.idProducto) : "",
				idCategoria: initialVoucher.idCategoria ? String(initialVoucher.idCategoria) : "",
				producto: initialVoucher.producto || null,
				categoria: initialVoucher.categoria || null,
				cantidad: initialVoucher.cantidad ?? null,
				limiteDiario: initialVoucher.limiteDiario ?? null,
			};
			const clientesMap = new Map();
			sourceVouchers.forEach((voucher) => {
				if (voucher?.cliente?.id) clientesMap.set(Number(voucher.cliente.id), voucher.cliente);
			});
			const nextClientes = Array.from(clientesMap.values());
			setForm(nextForm);
			setManualSelectedClientes(nextClientes);
			setEditorBaseline(buildVoucherEditorFingerprint(nextForm, nextClientes));
		} else {
			const nextForm = createDefaultVoucherForm();
			setForm(nextForm);
			setManualSelectedClientes([]);
			setEditorBaseline(buildVoucherEditorFingerprint(nextForm, []));
		}
	}, [open, initialVoucher, sourceVouchers]);

	useEffect(() => {
		if (!searchExecuted) {
			lastAvailableSearchRef.current = transferFilters.available;
			return undefined;
		}
		if (lastAvailableSearchRef.current === transferFilters.available) return undefined;
		lastAvailableSearchRef.current = transferFilters.available;

		const timeoutId = setTimeout(() => {
			fetchClientesResultados(appliedClientesFilters, transferFilters.available)
				.then((clientes) => {
					setClientesResultados(clientes);
					setAvailableCheckedClientes({});
					setClientesPage(1);
				})
				.catch((error) => {
					handleSnackbar(error?.message || "No se pudieron cargar los clientes", "error");
				});
		}, 400);

		return () => clearTimeout(timeoutId);
	}, [appliedClientesFilters, buscarClientes, handleSnackbar, searchExecuted, transferFilters.available]);

	useEffect(() => {
		const nextPage = clientesPagesInfo.pages === 0 ? 1 : Math.min(clientesPage, clientesPagesInfo.pages);
		if (clientesPage !== nextPage) setClientesPage(nextPage);
	}, [clientesPage, clientesPagesInfo.pages]);

	const fetchClientesResultados = async (filters, searchValue) => {
		const filter = buildClientesFilter(filters);
		const variables = {
			filter: Object.keys(filter).length ? filter : null,
			search: searchValue.trim() || null,
			limit: AVAILABLE_CLIENTES_PAGE_SIZE,
		};
		const firstResponse = await buscarClientes({ variables: { ...variables, page: 1 } });
		const clientesMap = new Map();
		(firstResponse?.data?.clientes || []).forEach((cliente) => {
			clientesMap.set(Number(cliente.id), cliente);
		});
		const totalPages = Number(firstResponse?.data?.paginationInfo?.pages || 0);

		for (let nextPage = 2; nextPage <= totalPages; nextPage += 1) {
			const response = await buscarClientes({ variables: { ...variables, page: nextPage } });
			(response?.data?.clientes || []).forEach((cliente) => {
				clientesMap.set(Number(cliente.id), cliente);
			});
		}

		return Array.from(clientesMap.values());
	};

	const handleSearchClientes = async () => {
		setSearchExecuted(true);
		setClientesPage(1);
		setAppliedClientesFilters({ ...clientesFilters });
		setAvailableCheckedClientes({});
		lastAvailableSearchRef.current = transferFilters.available;

		try {
			const clientes = await fetchClientesResultados(clientesFilters, transferFilters.available);
			setClientesResultados(clientes);
		} catch (error) {
			handleSnackbar(error?.message || "No se pudieron cargar los clientes", "error");
		}
	};

	const handleAvailablePageChange = (nextPage) => {
		setClientesPage(nextPage);
	};

	const toggleChecked = (setIds, id) => setIds((prev) => prev.includes(Number(id)) ? prev.filter((item) => item !== Number(id)) : [...prev, Number(id)]);
	const moveToSelected = () => {
		const nuevos = Object.values(availableCheckedClientes || {});
		if (!nuevos.length) return;
		setManualSelectedClientes((prev) => sortClientes([...prev, ...nuevos.filter((cliente) => !prev.some((item) => Number(item.id) === Number(cliente.id)))]));
		setAvailableCheckedClientes({});
	};
	const moveToAvailable = () => {
		if (!selectedCheckedIds.length) return;
		const checked = new Set(selectedCheckedIds.map(Number));
		setManualSelectedClientes((prev) => prev.filter((cliente) => !checked.has(Number(cliente.id))));
		setSelectedCheckedIds([]);
	};
	const toggleAllAvailable = () => {
		if (!availableVisibleIds.length) return;
		setAvailableCheckedClientes((prev) => {
			if (availableAllChecked) {
				return Object.fromEntries(Object.entries(prev).filter(([key]) => !availableVisibleIds.includes(Number(key))));
			}

			const next = { ...prev };
			availableClientes.forEach((cliente) => {
				next[Number(cliente.id)] = cliente;
			});
			return next;
		});
	};
	const toggleAllSelected = () => {
		if (!movableSelectedVisibleIds.length) return;
		setSelectedCheckedIds((prev) => {
			if (selectedAllChecked) {
				return prev.filter((id) => !movableSelectedVisibleIds.includes(Number(id)));
			}

			return Array.from(new Set([...prev.map(Number), ...movableSelectedVisibleIds]));
		});
	};

	const handleDesdeChange = (value) => {
		setForm((prev) => {
			if (!value) return { ...prev, desde: value };
			const currentDuration = getVoucherDurationDays(prev.desde, prev.hasta);
			return {
				...prev,
				desde: value,
				hasta: getVoucherHastaFromDesde(value, currentDuration == null ? 1 : currentDuration, currentDuration == null ? "month" : "day"),
			};
		});
	};

	const handleHastaChange = (value) => {
		setForm((prev) => ({ ...prev, hasta: value }));
	};

	const handleCantidadDiasChange = (e) => {
		const rawValue = e.target.value;
		if (rawValue == null || rawValue === "" || !form.desde) return;
		const nextDays = Math.max(0, Math.floor(Number(rawValue)));
		setForm((prev) => ({
			...prev,
			hasta: getVoucherHastaFromDesde(prev.desde, nextDays, "day"),
		}));
	};

	const openTargetSelector = (tipo) => {
		setTargetSelector({ open: true, tipo });
	};

	const handleTipoChange = (nextTipo) => {
		if (nextTipo === "MONTO") {
			setForm((prev) => ({ ...prev, tipo: "MONTO", idProducto: "", producto: null, idCategoria: "", categoria: null }));
			return;
		}
		openTargetSelector(nextTipo);
	};

	const closeTargetSelector = () => {
		setTargetSelector({ open: false, tipo: null });
	};

	const handleRemoveTarget = () => {
		setForm((prev) => ({
			...prev,
			tipo: "MONTO",
			idProducto: "",
			producto: null,
			idCategoria: "",
			categoria: null,
		}));
	};

	const handleProductoSelected = (producto) => {
		setForm((prev) => ({
			...prev,
			tipo: "PRODUCTO",
			idProducto: String(producto.id),
			producto,
			idCategoria: "",
			categoria: null,
		}));
		closeTargetSelector();
	};

	const handleCategoriaSelected = (categoria) => {
		setForm((prev) => ({
			...prev,
			tipo: "CATEGORIA",
			idCategoria: String(categoria.id),
			categoria,
			idProducto: "",
			producto: null,
		}));
		closeTargetSelector();
	};

	const getEditedConfigurationFields = () => ({
		desde: form.desde,
		hasta: form.hasta,
		tipo: form.tipo,
		idProducto: form.tipo === "PRODUCTO" ? Number(form.idProducto) : null,
		producto: form.tipo === "PRODUCTO" ? form.producto : null,
		idCategoria: form.tipo === "CATEGORIA" ? Number(form.idCategoria) : null,
		categoria: form.tipo === "CATEGORIA" ? form.categoria : null,
		cantidad: Number(form.cantidad),
		limiteDiario: Number(form.limiteDiario),
	});

	const buildSubmissionItems = () => {
		const configurationFields = getEditedConfigurationFields();
		const existingItems = sourceVouchers.map((voucher) => ({
			replaceTempId: voucher.tempId,
			voucher: {
				...voucher,
				...configurationFields,
				estadoLocal: voucher.id ? "Editado" : "Pendiente",
			},
		}));
		const addedClientes = isEditingVoucher
			? selectedClientes.filter((cliente) => !lockedClienteIdSet.has(Number(cliente.id)))
			: selectedClientes;
		const createdAt = Date.now();
		const addedItems = addedClientes.map((cliente, index) => ({
			replaceTempId: null,
			voucher: {
				tempId: `temp-${createdAt}-${cliente.id}-${index}`,
				id: null,
				idCliente: Number(cliente.id),
				cliente,
				...configurationFields,
				habilitado: true,
				tieneUso: false,
				tieneUsoActivo: false,
				estadoLocal: "Pendiente",
			},
		}));

		return isEditingVoucher ? [...existingItems, ...addedItems] : addedItems;
	};

	const handleSubmit = () => {
		const canSubmit = validForm && selectedClientes.length > 0;
		if (!canSubmit) return;
		onSubmit(buildSubmissionItems().map(({ replaceTempId, voucher }) => ({ replaceTempId, voucher })));
	};

	const requestCloseEditor = () => {
		if (hasUnsavedEditorChanges) {
			setDiscardEditorDialogOpen(true);
			return;
		}
		onClose();
	};

	const discardEditorChanges = () => {
		setDiscardEditorDialogOpen(false);
		onClose();
	};

	const amountAdornment = form.tipo === "MONTO" ? <InputAdornment position="start">$</InputAdornment> : null;
	const canSubmit = validForm && selectedClientes.length > 0;

	return (
		<Dialog open={open} fullWidth maxWidth="xl">
			<DialogContent sx={{ p: { xs: 2, md: 3 } }}>
				<SoftBox display="flex" justifyContent="space-between" alignItems="start" mb={2}>
					<SoftBox>
						<SoftTypography variant="h5" fontWeight="bold">{isEditingVoucher ? "Editar varios vouchers" : "Agregar beneficio/voucher"}</SoftTypography>
						<SoftTypography variant="caption" color="secondary">{isEditingVoucher ? "Los clientes ya asignados se conservan; puede agregar nuevos clientes." : "Configure el voucher y seleccione clientes."}</SoftTypography>
					</SoftBox>
					<SoftButton color="secondary" size="small" circular iconOnly onClick={requestCloseEditor}><Icon>close</Icon></SoftButton>
				</SoftBox>

				<Card sx={{ p: 2, mb: 2, boxShadow: "none", border: "1px solid rgba(148,163,184,.16)" }}>
					<Grid container spacing={1.5}>
						<Grid item xs={12} md={3}><InputLabel><SoftTypography variant="caption" fontWeight="bold">Desde</SoftTypography></InputLabel><SoftInput type="date" value={form.desde} onChange={(e) => handleDesdeChange(e.target.value)} inputProps={{ min: todayDate }} error={Boolean(form.desde) && dayjs(form.desde).isBefore(dayjs(todayDate), "day")} /></Grid>
						<Grid item xs={12} md={3}><InputLabel><SoftTypography variant="caption" fontWeight="bold">Hasta</SoftTypography></InputLabel><SoftInput type="date" value={form.hasta} onChange={(e) => handleHastaChange(e.target.value)} inputProps={{ min: form.desde || todayDate }} error={Boolean(form.desde) && Boolean(form.hasta) && dayjs(form.hasta).isBefore(dayjs(form.desde), "day")} /></Grid>
						<Grid item xs={12} md={2}><InputLabel><SoftTypography variant="caption" fontWeight="bold">Cantidad de dias</SoftTypography></InputLabel><SoftInputNumber value={cantidadDias} onChange={handleCantidadDiasChange} inputProps={{ min: 0 }} /></Grid>
						<Grid item xs={12} md={4}>
							<InputLabel htmlFor="voucher-type"><SoftTypography variant="caption" fontWeight="bold">Tipo</SoftTypography></InputLabel>
							<Select id="voucher-type" fullWidth value={form.tipo} onChange={(e) => handleTipoChange(e.target.value)}>
								<MenuItem value="MONTO">Monto</MenuItem>
								<MenuItem value="PRODUCTO">Producto</MenuItem>
								<MenuItem value="CATEGORIA">Categoría</MenuItem>
							</Select>
						</Grid>
						{form.tipo !== "MONTO" ? (
							<Grid item xs={12}>
								<SoftBox display="flex" alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" flexDirection={{ xs: "column", sm: "row" }} gap={1.25} p={1.5} sx={{ border: "1px solid rgba(37,99,235,.22)", borderRadius: "14px", backgroundColor: "rgba(37,99,235,.04)" }}>
									<SoftBox display="flex" alignItems="center" gap={1.1}>
										<SoftBox
											width="2.5rem"
											height="2.5rem"
											borderRadius="lg"
											bgColor="info"
											variant="gradient"
											display="flex"
											alignItems="center"
											justifyContent="center"
											sx={{ flexShrink: 0, lineHeight: 1 }}
										>
											<Icon sx={{ color: "#fff", display: "block", lineHeight: 1 }}>
												{form.tipo === "PRODUCTO" ? "inventory_2" : "category"}
											</Icon>
										</SoftBox>
										<SoftBox>
											<SoftTypography variant="caption" color="secondary" display="block">{form.tipo === "PRODUCTO" ? "Producto seleccionado" : "Categoría seleccionada"}</SoftTypography>
											<SoftTypography variant="button" fontWeight="bold">{form.tipo === "PRODUCTO" ? form.producto?.nombre || "Sin seleccionar" : form.categoria?.categoria || "Sin seleccionar"}</SoftTypography>
										</SoftBox>
									</SoftBox>
									<SoftBox display="flex" gap={1}>
										<SoftButton color="white" size="small" circular onClick={() => openTargetSelector(form.tipo)}><Icon>edit</Icon>&nbsp;Cambiar</SoftButton>
										<SoftButton color="secondary" size="small" circular onClick={handleRemoveTarget}><Icon>close</Icon>&nbsp;Quitar</SoftButton>
									</SoftBox>
								</SoftBox>
							</Grid>
						) : null}
						<Grid item xs={12} md={6}><InputLabel><SoftTypography variant="caption" fontWeight="bold">{form.tipo === "MONTO" ? "Monto total en pesos" : "Monto total en unidades"}</SoftTypography></InputLabel><SoftInputNumber value={form.cantidad} onChange={(e) => setForm((prev) => ({ ...prev, cantidad: e.target.value }))} InputProps={{ startAdornment: amountAdornment }} /></Grid>
						<Grid item xs={12} md={6}><InputLabel><SoftTypography variant="caption" fontWeight="bold">{form.tipo === "MONTO" ? "Límite diario en pesos" : "Límite diario en unidades"}</SoftTypography></InputLabel><SoftInputNumber value={form.limiteDiario} onChange={(e) => setForm((prev) => ({ ...prev, limiteDiario: e.target.value }))} InputProps={{ startAdornment: amountAdornment }} /></Grid>
					</Grid>
				</Card>

				<Card sx={{ p: 2, boxShadow: "none", border: "1px solid rgba(148,163,184,.16)" }}>
					<Grid container spacing={1.5} mb={1}>
						<Grid item xs={12} md={2}><InputLabel><SoftTypography variant="caption" fontWeight="bold">Nombre</SoftTypography></InputLabel><SoftInput value={clientesFilters.nombre} onChange={(e) => setClientesFilters((prev) => ({ ...prev, nombre: e.target.value }))} /></Grid>
						<Grid item xs={12} md={2}><InputLabel><SoftTypography variant="caption" fontWeight="bold">Apellido</SoftTypography></InputLabel><SoftInput value={clientesFilters.apellido} onChange={(e) => setClientesFilters((prev) => ({ ...prev, apellido: e.target.value }))} /></Grid>
						<Grid item xs={12} md={2}><InputLabel><SoftTypography variant="caption" fontWeight="bold">DNI</SoftTypography></InputLabel><SoftInput value={clientesFilters.dni} onChange={(e) => setClientesFilters((prev) => ({ ...prev, dni: e.target.value }))} /></Grid>
						<Grid item xs={12} md={2}><InputLabel><SoftTypography variant="caption" fontWeight="bold">Aplicacion</SoftTypography></InputLabel><Select fullWidth value={clientesFilters.appUsuario} onChange={(e) => setClientesFilters((prev) => ({ ...prev, appUsuario: e.target.value, tipoUsuario: "" }))}><MenuItem value="">Todas</MenuItem>{APP_USUARIO_FILTER_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></Grid>
						<Grid item xs={12} md={2}><InputLabel><SoftTypography variant="caption" fontWeight="bold">Tipo user</SoftTypography></InputLabel><Select fullWidth value={clientesFilters.tipoUsuario} disabled={!clientesFilters.appUsuario} onChange={(e) => setClientesFilters((prev) => ({ ...prev, tipoUsuario: e.target.value }))}><MenuItem value="">Todos</MenuItem>{(TIPO_USUARIO_FILTER_OPTIONS[clientesFilters.appUsuario] || []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></Grid>
						<Grid item xs={12} md={2}><InputLabel><SoftTypography variant="caption" fontWeight="bold">Acciones</SoftTypography></InputLabel><SoftBox display="flex" gap={1} pt={0.3}><SoftButton color="white" size="small" circular iconOnly onClick={() => { setClientesFilters(DEFAULT_CLIENT_FILTERS); setTransferFilters((prev) => ({ ...prev, available: "" })); setAppliedClientesFilters(DEFAULT_CLIENT_FILTERS); setClientesPage(1); setClientesResultados([]); setAvailableCheckedClientes({}); setSearchExecuted(false); }}><Icon>filter_alt_off</Icon></SoftButton><SoftButton color="uproGreen" size="small" circular iconOnly onClick={handleSearchClientes} sx={{ backgroundColor: `${ACCESSIBLE_GREEN} !important` }}><Icon>search</Icon></SoftButton></SoftBox></Grid>
					</Grid>
					<Grid container spacing={1.5} alignItems="stretch">
						<Grid item xs={12} lg={5}>
							<SoftTypography variant="button" fontWeight="bold">Resultados disponibles</SoftTypography>
							<SoftInput placeholder="Buscar en resultados" value={transferFilters.available} onChange={(e) => setTransferFilters((prev) => ({ ...prev, available: e.target.value }))} icon={{ component: "search", direction: "left" }} />
							<SoftBox mt={1.5} sx={{ border: "1px solid rgba(148,163,184,.16)", borderRadius: "12px", minHeight: "320px", maxHeight: "320px", overflow: "auto" }}>
								<SoftBox px={1.25} py={0.95} display="flex" alignItems="center" gap={0.8} sx={{ borderBottom: "1px solid rgba(148,163,184,.14)", background: "rgba(248,250,252,.65)" }}>
									<Checkbox checked={availableAllChecked} indeterminate={availableIndeterminate} onChange={toggleAllAvailable} disabled={loadingClientes || !searchExecuted || availableVisibleIds.length === 0} sx={{ p: 0.5 }} />
									<SoftTypography variant="caption" fontWeight="medium" color="secondary">Seleccionar todos</SoftTypography>
								</SoftBox>
								{loadingClientes ? <SoftBox p={2}><Loading /></SoftBox> : !searchExecuted ? <SoftBox minHeight="272px" display="grid" sx={{ placeItems: "center" }}><SoftTypography variant="button" color="secondary" m={2}>Busque clientes para iniciar</SoftTypography></SoftBox> : availableClientes.length === 0 ? <SoftBox p={2}><SinDatos /></SoftBox> : <List disablePadding>{availablePageClientes.map((cliente) => <ListItemButton key={cliente.id} dense onClick={() => setAvailableCheckedClientes((prev) => prev[Number(cliente.id)] ? Object.fromEntries(Object.entries(prev).filter(([key]) => Number(key) !== Number(cliente.id))) : { ...prev, [Number(cliente.id)]: cliente })} sx={{ alignItems: "flex-start", px: 1.25, py: 1.1, borderBottom: "1px solid rgba(148,163,184,.14)", "&:last-of-type": { borderBottom: "none" } }}><ListItemIcon sx={{ minWidth: 30, mr: 0.75, pt: 0.4 }}><Checkbox checked={availableCheckedSet.has(Number(cliente.id))} tabIndex={-1} disableRipple sx={{ p: 0.5 }} /></ListItemIcon><ListItemText primary={renderClienteOption(cliente)} sx={{ my: 0 }} /></ListItemButton>)}</List>}
							</SoftBox>
							{searchExecuted && clientesPagesInfo.pages > 0 ? <CompactPagination loading={loadingClientes} length={availablePageClientes.length} total={clientesPagesInfo.total} pages={clientesPagesInfo.pages} page={normalizedClientesPage} setPage={handleAvailablePageChange} noCard /> : null}
						</Grid>
						<Grid item xs={12} lg={2}><SoftBox height="100%" display="flex" justifyContent="center" alignItems="center" gap={1.1} flexDirection={{ xs: "row", lg: "column" }}><SoftButton color="uproGreen" size="small" circular iconOnly onClick={moveToSelected} disabled={!Object.keys(availableCheckedClientes).length} sx={{ backgroundColor: `${ACCESSIBLE_GREEN} !important` }}><Icon>{isDesktop ? "chevron_right" : "expand_more"}</Icon></SoftButton><SoftButton color="white" size="small" circular iconOnly onClick={moveToAvailable} disabled={!selectedCheckedIds.length}><Icon>{isDesktop ? "chevron_left" : "expand_less"}</Icon></SoftButton></SoftBox></Grid>
						<Grid item xs={12} lg={5}>
							<SoftTypography variant="button" fontWeight="bold">{`Clientes seleccionados (${selectedClientes.length})`}</SoftTypography>
							<SoftInput placeholder="Filtrar seleccionados" value={transferFilters.selected} onChange={(e) => setTransferFilters((prev) => ({ ...prev, selected: e.target.value }))} icon={{ component: "filter_alt", direction: "left" }} />
							<SoftBox mt={1.5} sx={{ border: "1px solid rgba(148,163,184,.16)", borderRadius: "12px", minHeight: "320px", maxHeight: "320px", overflow: "auto" }}>
								<SoftBox px={1.25} py={0.95} display="flex" alignItems="center" gap={0.8} sx={{ borderBottom: "1px solid rgba(148,163,184,.14)", background: "rgba(248,250,252,.65)" }}>
									<Checkbox checked={selectedAllChecked} indeterminate={selectedIndeterminate} onChange={toggleAllSelected} disabled={movableSelectedVisibleIds.length === 0} sx={{ p: 0.5 }} />
									<SoftTypography variant="caption" fontWeight="medium" color="secondary">Seleccionar todos</SoftTypography>
								</SoftBox>
								{selectedFiltered.length === 0 ? <SoftBox minHeight="272px" display="grid" sx={{ placeItems: "center" }}><SoftTypography variant="button" m={2} color="secondary">No hay clientes seleccionados</SoftTypography></SoftBox> : (
									<List disablePadding>
										{selectedFiltered.map((cliente) => {
											const isLocked = lockedClienteIdSet.has(Number(cliente.id));
											return (
												<ListItemButton
													key={cliente.id}
													dense
													disabled={isLocked}
													onClick={() => toggleChecked(setSelectedCheckedIds, cliente.id)}
													sx={{ alignItems: "flex-start", px: 1.25, py: 1.1, borderBottom: "1px solid rgba(148,163,184,.14)", opacity: "1 !important", "&:last-of-type": { borderBottom: "none" } }}
												>
													<ListItemIcon sx={{ minWidth: 30, mr: 0.75, pt: 0.4 }}>
														<Checkbox checked={selectedCheckedSet.has(Number(cliente.id))} disabled={isLocked} tabIndex={-1} disableRipple sx={{ p: 0.5 }} />
													</ListItemIcon>
													<ListItemText
														primary={renderClienteOption(cliente)}
														secondary={isLocked ? "Cliente ya asignado; no puede quitarse durante la edición." : null}
														sx={{ my: 0, "& .MuiListItemText-secondary": { color: "#64748b", fontSize: "0.72rem" } }}
													/>
												</ListItemButton>
											);
										})}
									</List>
								)}
							</SoftBox>
						</Grid>
					</Grid>
				</Card>

				<SoftBox mt={2} pt={2} display="flex" justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={1.25} sx={{ borderTop: "1px solid rgba(148,163,184,.18)" }}>
					<SoftBox>
						{!validForm ? <SoftTypography variant="caption" color="error">Complete vigencia, tipo, destino cuando corresponda, cantidad y límite diario.</SoftTypography> : null}
						{validForm && !canSubmit ? <SoftTypography variant="caption" color="error">Seleccione al menos un cliente.</SoftTypography> : null}
					</SoftBox>
					<SoftBox display="flex" gap={1.1}><SoftButton color="secondary" size="small" circular onClick={requestCloseEditor}><Icon>cancel</Icon>&nbsp;Cancelar</SoftButton><SoftButton color="primary" size="small" circular disabled={!canSubmit} onClick={handleSubmit}>{isEditingVoucher ? "Guardar vouchers" : "Agregar al grupo"}&nbsp;<Icon>done</Icon></SoftButton></SoftBox>
				</SoftBox>

				<UnsavedChangesDialog
					open={discardEditorDialogOpen}
					onClose={() => setDiscardEditorDialogOpen(false)}
					description="Hay cambios en la configuración o en los clientes agregados. Si sale ahora, se perderán."
					dialogId="discard-voucher-editor"
					onDiscard={discardEditorChanges}
				/>

				<CategoriaVoucherSelectorDialog
					open={targetSelector.open && targetSelector.tipo === "CATEGORIA"}
					categorias={categorias}
					selectedId={form.tipo === "CATEGORIA" ? form.idCategoria : null}
					onCancel={closeTargetSelector}
					onConfirm={handleCategoriaSelected}
				/>
				<ProductoVoucherSelectorDialog
					open={targetSelector.open && targetSelector.tipo === "PRODUCTO"}
					categorias={categorias}
					selectedProducto={form.tipo === "PRODUCTO" ? form.producto : null}
					onCancel={closeTargetSelector}
					onConfirm={handleProductoSelected}
				/>
			</DialogContent>
		</Dialog>
	);
}

function BeneficiosVouchers() {
	const { handleSnackbar } = useContext(MessageManager);
	const { menu } = useContext(MenuContext);
	const { verificarAcceso } = useContext(UserContext);
	const isCompactGroupTable = useMediaQuery((theme) => theme.breakpoints.down("sm"));
	const canViewModule = verificarAcceso(BENEFICIOS_VOUCHERS_PERMISSION_IDS, "or");
	const canEditGroups = verificarAcceso(BENEFICIOS_VOUCHERS_PERMISSIONS.EDIT);
	const canChangeGroupStatus = verificarAcceso(BENEFICIOS_VOUCHERS_PERMISSIONS.CHANGE_STATUS);
	const canDeleteGroups = verificarAcceso(BENEFICIOS_VOUCHERS_PERMISSIONS.DELETE);
	const canInvoiceGroups = verificarAcceso(BENEFICIOS_VOUCHERS_PERMISSIONS.INVOICE);
	const canManageBillingClients = verificarAcceso(BENEFICIOS_VOUCHERS_PERMISSIONS.MANAGE_BILLING_CLIENTS);
	const apolloClient = useApolloClient();
	const orderFileInputRef = useRef(null);
	const groupTableContainerRef = useRef(null);
	const groupColumnsCustomizedRef = useRef(false);
	const handledVoucherLogEventsRef = useRef({ nuevoPedido: null, pedidoModificado: null });
	const voucherLogMetricsRequestRef = useRef(0);
	const [openGroupModal, setOpenGroupModal] = useState(false);
	const [openVoucherModal, setOpenVoucherModal] = useState(false);
	const [openVoucherClienteModal, setOpenVoucherClienteModal] = useState(false);
	const [openVouchersAsignadosDialog, setOpenVouchersAsignadosDialog] = useState(false);
	const [openInactiveDialog, setOpenInactiveDialog] = useState(false);
	const [groupDeleteDialog, setGroupDeleteDialog] = useState({ open: false, group: null, confirmValue: "", motivo: "" });
	const [voucherDeleteDialog, setVoucherDeleteDialog] = useState({ open: false, voucher: null, confirmValue: "" });
	const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
	const [selectedVoucherTempIds, setSelectedVoucherTempIds] = useState([]);
	const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false, confirmValue: "" });
	const [bulkEditMode, setBulkEditMode] = useState(false);
	const [selectedConfigurationKey, setSelectedConfigurationKey] = useState("");
	const [editingConfiguration, setEditingConfiguration] = useState(null);
	const [groupDraftBaseline, setGroupDraftBaseline] = useState("");
	const [discardGroupDialogOpen, setDiscardGroupDialogOpen] = useState(false);
	const [groupForm, setGroupForm] = useState(DEFAULT_GROUP_FORM);
	const [persistedGroupState, setPersistedGroupState] = useState("Borrador");
	const [persistedGroupName, setPersistedGroupName] = useState("");
	const [previewState, setPreviewState] = useState({ open: false, url: "", mode: null, title: "Preview del adjunto", revokeOnClose: false });
	const [stagedVouchers, setStagedVouchers] = useState([]);
	const [groupTableFilters, setGroupTableFilters] = useState(DEFAULT_GROUP_TABLE_FILTERS);
	const [groupTableFiltersExpanded, setGroupTableFiltersExpanded] = useState(!isCompactGroupTable);
	const [visibleGroupColumnFields, setVisibleGroupColumnFields] = useState(
		isCompactGroupTable ? COMPACT_VISIBLE_GROUP_COLUMN_FIELDS : DEFAULT_VISIBLE_GROUP_COLUMN_FIELDS
	);
	const [groupTableContainerWidth, setGroupTableContainerWidth] = useState(0);
	const [voucherTableFilters, setVoucherTableFilters] = useState(DEFAULT_VOUCHER_TABLE_FILTERS);
	const [voucherTablePagination, setVoucherTablePagination] = useState(DEFAULT_VOUCHER_TABLE_PAGINATION);
	const [groupEditorTab, setGroupEditorTab] = useState("detalles");
	const [groupSectionExpanded, setGroupSectionExpanded] = useState(false);
	const [voucherLogFilters, setVoucherLogFilters] = useState(DEFAULT_VOUCHER_LOG_FILTERS);
	const [debouncedVoucherLogSearch, setDebouncedVoucherLogSearch] = useState("");
	const [voucherLogPage, setVoucherLogPage] = useState(1);
	const [voucherLogMetricRows, setVoucherLogMetricRows] = useState([]);
	const [loadingVoucherLogMetrics, setLoadingVoucherLogMetrics] = useState(false);
	const { data: dataGrupos, loading: loadingGrupos, refetch: refetchGrupos } = useQuery(VOUCHER_GRUPOS_QUERY, { skip: !canViewModule, fetchPolicy: "network-only" });
	const { data: dataCategorias } = useQuery(CATEGORIAS_QUERY, { skip: !canViewModule, fetchPolicy: "cache-first" });
	const { data: dataSedes } = useQuery(SEDES_VOUCHER_GRUPO_QUERY, { skip: !canViewModule, fetchPolicy: "cache-first" });
	const { data: dataVoucherClientes, refetch: refetchVoucherClientes } = useQuery(VOUCHER_CLIENTES_GRUPO_QUERY, { variables: { page: 1, limit: 100 }, skip: !canViewModule, fetchPolicy: "network-only" });
	const [loadVoucherGrupoDetail, { loading: loadingGroupDetail }] = useLazyQuery(VOUCHER_GRUPO_DETAIL_QUERY, { fetchPolicy: "network-only" });
	const [loadVoucherGrupoUsoLog, { data: voucherLogData, loading: loadingVoucherLog }] = useLazyQuery(VOUCHER_GRUPO_USO_LOG_QUERY, { fetchPolicy: "network-only" });
	const { data: nuevoPedidoData } = useSubscription(NUEVO_PEDIDO_SUBSCRIPTION, {
		skip: !canViewModule || !openGroupModal || groupEditorTab !== "log",
	});
	const { data: pedidoModificadoData } = useSubscription(PEDIDO_MODIFICADO_SUBSCRIPTION, {
		skip: !canViewModule || !openGroupModal || groupEditorTab !== "log",
	});
	const [crearGrupoConVouchers, { loading: loadingCrear }] = useMutation(CREAR_GRUPO_CON_VOUCHERS);
	const [actualizarEstadoVoucherGrupo] = useMutation(ACTUALIZAR_ESTADO_GRUPO);
	const [eliminarVoucherGrupo, { loading: loadingEliminarGrupo }] = useMutation(ELIMINAR_VOUCHER_GRUPO);
	const [eliminarVoucher, { loading: loadingEliminarVoucher }] = useMutation(ELIMINAR_VOUCHER);
	const [eliminarVouchers, { loading: loadingEliminarVouchers }] = useMutation(ELIMINAR_VOUCHERS);
	const grupos = dataGrupos?.voucherGrupos || [];
	const categorias = dataCategorias?.categoriasProducto || [];
	const sedes = dataSedes?.sedes || [];
	const voucherClientes = dataVoucherClientes?.voucherClientes?.rows || [];
	const voucherConfigurations = useMemo(() => {
		const configurationsMap = new Map();
		stagedVouchers.forEach((voucher) => {
			const key = getVoucherConfigurationKey(voucher);
			if (!configurationsMap.has(key)) {
				configurationsMap.set(key, {
					key,
					voucher,
					vouchers: [],
					tieneUso: false,
				});
			}
			const configuration = configurationsMap.get(key);
			configuration.vouchers.push(voucher);
			if (voucher.tieneUso) configuration.tieneUso = true;
		});

		return Array.from(configurationsMap.values())
			.map((configuration) => ({
				...configuration,
				label: formatVoucherConfigurationLabel(configuration.voucher),
			}))
			.sort((left, right) => left.label.localeCompare(right.label, "es"));
	}, [stagedVouchers]);
	const currentGroupDraftFingerprint = useMemo(
		() => buildGroupDraftFingerprint(groupForm, stagedVouchers),
		[groupForm, stagedVouchers]
	);
	const hasUnsavedGroupChanges = Boolean(
		openGroupModal
		&& groupDraftBaseline
		&& currentGroupDraftFingerprint !== groupDraftBaseline
	);
	const isEditingGroup = Boolean(groupForm.id);
	const currentGroupState = groupForm.estado || "Borrador";
	const groupFieldsDisabled = !canEditGroups || loadingCrear || (isEditingGroup && Boolean(groupForm.tieneUso));
	const VoucherEditor = groupForm.modalidad === "FISICO" ? VoucherFisicoBuilder : VoucherBuilderModal;
	const groupClientDisabled = !canEditGroups || loadingCrear;
	const groupStatusDisabled = !canEditGroups || !canChangeGroupStatus || loadingCrear;
	const groupAttachmentsDisabled = !canEditGroups || loadingCrear || (isEditingGroup && Boolean(groupForm.tieneUso));
	const normalizedNroExpediente = groupForm.nroExpediente.trim();
	const groupHeaderValid = Boolean(groupForm.nombre.trim());
	const groupSedeValid = Boolean(Number(groupForm.idSede || 0));
	const duplicateExpediente = useMemo(
		() =>
			Boolean(normalizedNroExpediente) && grupos.some(
				(group) =>
					String(group?.nroExpediente || "").trim().toLowerCase() === normalizedNroExpediente.toLowerCase() &&
					Number(group.id) !== Number(groupForm.id || 0)
			),
		[grupos, groupForm.id, normalizedNroExpediente]
	);
	const invalidActiveWithoutVouchers = currentGroupState === "Activo" && stagedVouchers.length === 0;
	const canSubmitGroup = canEditGroups && groupHeaderValid && groupSedeValid && !duplicateExpediente && !invalidActiveWithoutVouchers && !loadingCrear;
	const canConfirmGroupDelete = Boolean(
		groupDeleteDialog.group
		&& groupDeleteDialog.confirmValue === String(groupDeleteDialog.group?.nombre || "")
		&& groupDeleteDialog.motivo.trim()
		&& !groupDeleteDialog.group?.tieneUso
		&& !loadingEliminarGrupo
	);
	const voucherDeleteExpectedDni = String(voucherDeleteDialog.voucher?.cliente?.dni || "").trim();
	const canConfirmVoucherDelete = Boolean(
		voucherDeleteDialog.voucher
		&& voucherDeleteDialog.confirmValue.trim() === voucherDeleteExpectedDni
		&& !voucherDeleteDialog.voucher.tieneUso
		&& !loadingEliminarVoucher
	);
	const bulkDeleteExpectedGroupName = isEditingGroup ? persistedGroupName : groupForm.nombre.trim();
	const canConfirmBulkDelete = Boolean(
		selectedVoucherTempIds.length
		&& bulkDeleteExpectedGroupName
		&& bulkDeleteDialog.confirmValue === bulkDeleteExpectedGroupName
		&& !stagedVouchers.some((voucher) => selectedVoucherTempIds.includes(voucher.tempId) && voucher.tieneUso)
		&& !loadingEliminarVouchers
	);
	const filteredStagedVouchers = useMemo(() => {
		const search = voucherTableFilters.search.trim().toLowerCase();
		return stagedVouchers.filter((voucher) => {
			const clienteNombre = `${voucher?.cliente?.apellido || ""} ${voucher?.cliente?.nombre || ""} ${voucher?.cliente?.dni || ""} ${voucher?.producto?.nombre || ""} ${voucher?.categoria?.categoria || ""} ${!voucher.idCliente ? "físico" : ""}`.toLowerCase();
			const matchesSearch = !search || clienteNombre.includes(search);
			const matchesTipo = !voucherTableFilters.tipo || voucher.tipo === voucherTableFilters.tipo;
			const matchesHabilitado = voucherTableFilters.habilitado === ""
				? true
				: voucherTableFilters.habilitado === "true"
					? voucher.habilitado !== false
					: voucher.habilitado === false;
			return matchesSearch && matchesTipo && matchesHabilitado;
		});
	}, [stagedVouchers, voucherTableFilters]);
	const visibleVouchers = useMemo(() => {
		const page = Math.max(1, voucherTablePagination.page || 1);
		const limit = Math.max(1, voucherTablePagination.limit || 10);
		const start = (page - 1) * limit;
		return filteredStagedVouchers.slice(start, start + limit);
	}, [filteredStagedVouchers, voucherTablePagination.limit, voucherTablePagination.page]);
	const selectedVoucherTempIdSet = useMemo(() => new Set(selectedVoucherTempIds), [selectedVoucherTempIds]);
	const selectedVouchers = useMemo(
		() => stagedVouchers.filter((voucher) => selectedVoucherTempIdSet.has(voucher.tempId)),
		[selectedVoucherTempIdSet, stagedVouchers]
	);
	const getVoucherDeleteDisabledReason = (voucher) => {
		if (!voucher?.id) return "";
		if (persistedGroupState === "Activo") return "No se pueden eliminar vouchers de un grupo activo";
		if (voucher.tieneUso) return "Tiene consumos registrados. Deshabilite el voucher para conservar su historial.";
		return "";
	};
	const showGroupTabs = isEditingGroup;
	const voucherLogFilter = useMemo(() => ({
		search: debouncedVoucherLogSearch || null,
		estadoPedido: voucherLogFilters.estadoPedido || null,
		appUsuario: voucherLogFilters.appUsuario || null,
		tipoUsuario: voucherLogFilters.tipoUsuario || null,
		tipoVoucher: voucherLogFilters.tipoVoucher || null,
		fechaDesde: voucherLogFilters.fechaDesde || null,
		fechaHasta: voucherLogFilters.fechaHasta || null,
	}), [
		debouncedVoucherLogSearch,
		voucherLogFilters.appUsuario,
		voucherLogFilters.estadoPedido,
		voucherLogFilters.fechaDesde,
		voucherLogFilters.fechaHasta,
		voucherLogFilters.tipoUsuario,
		voucherLogFilters.tipoVoucher,
	]);
	const voucherLogRows = voucherLogData?.voucherGrupoUsoLog?.rows || [];
	const voucherLogPagination = voucherLogData?.voucherGrupoUsoLog?.pagination
		|| { total: 0, pages: 0, page: 1, limit: VOUCHER_LOG_PAGE_SIZE };
	const voucherLogMetrics = useMemo(() => {
		return buildVoucherUsageMetrics(voucherLogMetricRows);
	}, [voucherLogMetricRows]);
	const voucherLogAssignedTotals = useMemo(() => stagedVouchers.reduce((totals, voucher) => {
		const assignedValue = Number(voucher.cantidad || 0);
		if (voucher.tipo === "MONTO") totals.amount += assignedValue;
		if (["PRODUCTO", "CATEGORIA"].includes(voucher.tipo)) totals.units += assignedValue;
		return totals;
	}, { amount: 0, units: 0 }), [stagedVouchers]);
	const voucherLogMetricCards = useMemo(() => [
		{
			key: "amount",
			title: "Vouchers monto",
			detail: "Monto consumido / asignado",
			valueLabel: "Consumo total",
			value: `${formatVoucherValue(voucherLogMetrics.consumedAmount, "MONTO")} / ${formatVoucherValue(voucherLogAssignedTotals.amount, "MONTO")}`,
			percentage: formatVoucherPercentage(voucherLogMetrics.consumedAmount, voucherLogAssignedTotals.amount),
			icon: "confirmation_number",
			color: "#2563eb",
		},
		{
			key: "units",
			title: "Vouchers especie",
			detail: "Unidades consumidas / asignadas",
			valueLabel: "Consumo total",
			value: `${formatVoucherUnitsCompact(voucherLogMetrics.consumedUnits)} / ${formatVoucherUnitsCompact(voucherLogAssignedTotals.units)}`,
			percentage: formatVoucherPercentage(voucherLogMetrics.consumedUnits, voucherLogAssignedTotals.units),
			secondaryLabel: "Total en pesos",
			secondaryValue: formatVoucherValue(voucherLogMetrics.speciesMonetaryTotal, "MONTO"),
			icon: "confirmation_number",
			secondaryIcon: "paid",
			color: "#059669",
			secondaryColor: "#059669",
		},
		{
			key: "beneficiaries",
			title: "Beneficiarios",
			value: voucherLogMetrics.beneficiaries.toLocaleString("es-AR"),
			detail: "clientes únicos",
			valueLabel: "Total registrado",
			icon: "groups",
			color: "#059669",
		},
		{
			key: "latest",
			title: "Último uso",
			value: voucherLogMetrics.latestUsage ? formatDateCell(voucherLogMetrics.latestUsage) : "Sin usos",
			detail: "fecha más reciente",
			valueLabel: "Fecha registrada",
			icon: "event_available",
			color: "#0284c7",
		},
	], [voucherLogAssignedTotals, voucherLogMetrics]);
	const voucherLogColumns = [
		{ name: "pedido", desc: "Pedido", align: "left" },
		{ name: "cliente", desc: "Cliente", align: "left" },
		{ name: "app", desc: "App / tipo usuario", align: "left" },
		{ name: "estado", desc: "Estado", align: "left" },
		{ name: "voucher", desc: "Voucher", align: "left" },
		{ name: "consumo", desc: "Unidades consumidas", align: "left" },
		{ name: "cubierto", desc: "Cubierto por voucher", align: "left" },
		{ name: "remanente", desc: "Pagado por cliente", align: "left" },
		{ name: "total", desc: "Total pedido", align: "left" },
		{ name: "fechaPedido", desc: "Fecha pedido", align: "left" }
	];
	const voucherLogTableRows = voucherLogRows.map((item) => {
		const statusBadge = getPedidoStatusBadgeProps(item);

		return {
			id: item.idVenta,
			hasBorder: true,
			pedido: `${item.idPedido}`,
			cliente: (
				<SoftBox display="flex" flexDirection="column" alignItems="start" justifyContent="center">
					<SoftTypography variant="button" fontWeight="bold">{getClienteNombreCompleto(item.cliente)}</SoftTypography>
					<SoftTypography variant="caption" color="secondary" display="block">DNI {item?.cliente?.dni || "-"}</SoftTypography>
				</SoftBox>
			),
			app: renderClienteIdentityChips(item.cliente),
			estado: <SoftBadge variant="color" color={statusBadge.color} badgeContent={statusBadge.label} />,
			voucher: (
				<SoftBox display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center" width="100%" sx={{ minWidth: 0 }}>
					<SoftTypography variant="button" fontWeight="bold" sx={{ width: "100%", textAlign: "left" }}>{item.nombreVoucher || `Voucher #${item.idVoucher}`}</SoftTypography>
					<SoftTypography variant="caption" color="secondary" display="block" sx={{ width: "100%", textAlign: "left" }}>{formatVoucherType(item.tipoVoucher)}{item.productoVoucher ? ` · ${item.productoVoucher}` : item.categoriaVoucher ? ` · ${item.categoriaVoucher}` : ""}</SoftTypography>
				</SoftBox>
			),
			consumo: formatVoucherValue(item.cantidadUtilizada, item.tipoVoucher),
			cubierto: formatVoucherValue(item.montoCubiertoVoucher, "MONTO"),
			remanente: formatVoucherValue(item.remanenteCliente, "MONTO"),
			total: formatVoucherValue(item.totalPedido, "MONTO"),
			fechaPedido: formatDateCell(item.fechaPedido),
			creado: item.creado ? dayjs(item.creado).format("DD/MM/YYYY HH:mm") : "-",
		};
	});

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setDebouncedVoucherLogSearch(voucherLogFilters.search.trim());
			setVoucherLogPage(1);
		}, 300);
		return () => clearTimeout(timeoutId);
	}, [voucherLogFilters.search]);
	useEffect(() => {
		if (!openGroupModal || !showGroupTabs || groupEditorTab !== "log" || !groupForm.id) return;
		loadVoucherGrupoUsoLog({
			variables: { idGrupo: Number(groupForm.id), filter: voucherLogFilter, page: voucherLogPage, limit: VOUCHER_LOG_PAGE_SIZE },
		});
	}, [openGroupModal, showGroupTabs, groupEditorTab, groupForm.id, voucherLogPage, voucherLogFilter, loadVoucherGrupoUsoLog]);
	useEffect(() => {
		const hasNewPedidoEvent = nuevoPedidoData
			&& handledVoucherLogEventsRef.current.nuevoPedido !== nuevoPedidoData;
		const hasModifiedPedidoEvent = pedidoModificadoData
			&& handledVoucherLogEventsRef.current.pedidoModificado !== pedidoModificadoData;

		if (!hasNewPedidoEvent && !hasModifiedPedidoEvent) return;
		handledVoucherLogEventsRef.current = {
			nuevoPedido: nuevoPedidoData || handledVoucherLogEventsRef.current.nuevoPedido,
			pedidoModificado: pedidoModificadoData || handledVoucherLogEventsRef.current.pedidoModificado,
		};

		if (!openGroupModal || !showGroupTabs || groupEditorTab !== "log" || !groupForm.id) return;

		loadVoucherGrupoUsoLog({
			variables: { idGrupo: Number(groupForm.id), filter: voucherLogFilter, page: voucherLogPage, limit: VOUCHER_LOG_PAGE_SIZE },
		});
	}, [nuevoPedidoData, pedidoModificadoData, openGroupModal, showGroupTabs, groupEditorTab, groupForm.id, voucherLogFilter, voucherLogPage, loadVoucherGrupoUsoLog]);
	useEffect(() => {
		const requestId = voucherLogMetricsRequestRef.current + 1;
		voucherLogMetricsRequestRef.current = requestId;

		if (!openGroupModal || !showGroupTabs || groupEditorTab !== "log" || !groupForm.id) {
			setVoucherLogMetricRows([]);
			setLoadingVoucherLogMetrics(false);
			return undefined;
		}

		const loadVoucherLogMetrics = async () => {
			setLoadingVoucherLogMetrics(true);
			try {
				const firstResponse = await apolloClient.query({
					query: VOUCHER_GRUPO_USO_LOG_QUERY,
					variables: { idGrupo: Number(groupForm.id), filter: voucherLogFilter, page: 1, limit: VOUCHER_LOG_METRICS_PAGE_SIZE },
					fetchPolicy: "network-only",
				});
				const firstResult = firstResponse?.data?.voucherGrupoUsoLog;
				const totalPages = Number(firstResult?.pagination?.pages || 0);
				const remainingResponses = totalPages > 1
					? await Promise.all(
						Array.from({ length: totalPages - 1 }, (_, index) => apolloClient.query({
							query: VOUCHER_GRUPO_USO_LOG_QUERY,
							variables: { idGrupo: Number(groupForm.id), filter: voucherLogFilter, page: index + 2, limit: VOUCHER_LOG_METRICS_PAGE_SIZE },
							fetchPolicy: "network-only",
						}))
					)
					: [];
				const metricRows = [firstResponse, ...remainingResponses].flatMap(
					(response) => response?.data?.voucherGrupoUsoLog?.rows || []
				);

				if (voucherLogMetricsRequestRef.current === requestId) {
					setVoucherLogMetricRows(metricRows);
				}
			} catch {
				if (voucherLogMetricsRequestRef.current === requestId) {
					setVoucherLogMetricRows([]);
				}
			} finally {
				if (voucherLogMetricsRequestRef.current === requestId) {
					setLoadingVoucherLogMetrics(false);
				}
			}
		};

		loadVoucherLogMetrics();
		return () => {
			if (voucherLogMetricsRequestRef.current === requestId) {
				voucherLogMetricsRequestRef.current += 1;
			}
		};
	}, [apolloClient, nuevoPedidoData, pedidoModificadoData, openGroupModal, showGroupTabs, groupEditorTab, groupForm.id, voucherLogFilter]);
	useEffect(() => {
		const total = filteredStagedVouchers.length;
		const pages = total === 0 ? 0 : Math.ceil(total / (voucherTablePagination.limit || 10));
		setVoucherTablePagination((prev) => {
			const nextPage = pages === 0 ? 1 : Math.min(prev.page, pages);
			if (prev.total === total && prev.pages === pages && prev.page === nextPage) return prev;
			return { ...prev, total, pages, page: nextPage };
		});
	}, [filteredStagedVouchers.length, voucherTablePagination.limit]);
	useEffect(() => {
		const availableTempIds = new Set(stagedVouchers.map((voucher) => voucher.tempId));
		setSelectedVoucherTempIds((prev) => prev.filter((tempId) => availableTempIds.has(tempId)));
	}, [stagedVouchers]);

	const closeGroupDeleteDialog = () => setGroupDeleteDialog({ open: false, group: null, confirmValue: "", motivo: "" });
	const closeVoucherDeleteDialog = () => setVoucherDeleteDialog({ open: false, voucher: null, confirmValue: "" });
	const closeBulkDeleteDialog = () => setBulkDeleteDialog({ open: false, confirmValue: "" });

	const toggleVoucherSelection = (voucher) => {
		if (getVoucherDeleteDisabledReason(voucher)) return;
		setSelectedVoucherTempIds((prev) => prev.includes(voucher.tempId)
			? prev.filter((tempId) => tempId !== voucher.tempId)
			: [...prev, voucher.tempId]);
	};

	const handleBulkDeleteButton = () => {
		if (!canDeleteGroups) return;
		if (!bulkDeleteMode) {
			setBulkEditMode(false);
			setSelectedConfigurationKey("");
			setBulkDeleteMode(true);
			setSelectedVoucherTempIds([]);
			return;
		}
		if (selectedVoucherTempIds.length === 0) {
			setBulkDeleteMode(false);
			return;
		}
		setBulkDeleteDialog({ open: true, confirmValue: "" });
	};

	const startBulkEdit = () => {
		if (!canEditGroups) return;
		setBulkDeleteMode(false);
		setSelectedVoucherTempIds([]);
		setBulkEditMode(true);
		setSelectedConfigurationKey("");
	};

	const cancelBulkEdit = () => {
		setBulkEditMode(false);
		setSelectedConfigurationKey("");
		setEditingConfiguration(null);
	};

	const selectConfigurationToEdit = (configurationKey) => {
		if (!canEditGroups) return;
		setSelectedConfigurationKey(configurationKey);
		const configuration = voucherConfigurations.find((item) => item.key === configurationKey);
		if (!configuration || configuration.tieneUso) return;
		setEditingConfiguration(configuration);
		setOpenVoucherModal(true);
	};

	const closePreviewDialog = () => {
		if (previewState.revokeOnClose && previewState.url) {
			URL.revokeObjectURL(previewState.url);
		}
		setPreviewState({ open: false, url: "", mode: null, title: "Preview del adjunto", revokeOnClose: false });
	};

	const openAttachmentPreview = ({ title, file = null, existingPath = "" }) => {
		const base = file || { name: existingPath, type: /\.pdf$/i.test(existingPath) ? "application/pdf" : "" };
		const mode = getPreviewMode(base);
		const url = file ? URL.createObjectURL(file) : getAdjuntoUrl(existingPath);
		setPreviewState({ open: true, url, mode, title, revokeOnClose: Boolean(file) });
	};

	const clearGroupState = () => {
		setGroupEditorTab("detalles");
		setGroupSectionExpanded(false);
		setVoucherLogFilters(DEFAULT_VOUCHER_LOG_FILTERS);
		setDebouncedVoucherLogSearch("");
		setVoucherLogPage(1);
		setGroupForm(DEFAULT_GROUP_FORM);
		setPersistedGroupState("Borrador");
		setPersistedGroupName("");
		setStagedVouchers([]);
		setBulkDeleteMode(false);
		setSelectedVoucherTempIds([]);
		setBulkEditMode(false);
		setSelectedConfigurationKey("");
		setEditingConfiguration(null);
		setOpenVoucherModal(false);
		setOpenVoucherClienteModal(false);
		setGroupDraftBaseline("");
		setDiscardGroupDialogOpen(false);
		setVoucherTableFilters(DEFAULT_VOUCHER_TABLE_FILTERS);
		setVoucherTablePagination(DEFAULT_VOUCHER_TABLE_PAGINATION);
		closeGroupDeleteDialog();
		closeVoucherDeleteDialog();
		closeBulkDeleteDialog();
		if (orderFileInputRef.current) orderFileInputRef.current.value = "";
		closePreviewDialog();
	};

	const openCreateGroupModal = () => {
		if (!canEditGroups) return;
		clearGroupState();
		setGroupDraftBaseline(buildGroupDraftFingerprint(DEFAULT_GROUP_FORM, []));
		setOpenGroupModal(true);
	};

	const openEditGroupModal = async (idGrupo) => {
		try {
			clearGroupState();
			const detailPromise = loadVoucherGrupoDetail({
				variables: { idGrupo: Number(idGrupo), page: 1, limit: 1000 },
			});
			setOpenGroupModal(true);
			const response = await detailPromise;
			const group = response?.data?.voucherGrupo;
			if (!group) throw new Error("No se pudo cargar el grupo seleccionado");
			setPersistedGroupState(group.estado || "Borrador");
			setPersistedGroupName(group.nombre || "");
			const nextGroupForm = {
				id: Number(group.id),
				modalidad: group.modalidad || "PERSONAL",
				nombre: group.nombre || "",
				descripcion: group.descripcion || "",
				nroExpediente: group.nroExpediente || "",
				estado: group.estado || "Borrador",
				ordenProvisionFile: null,
				existingOrdenProvisionPath: group.filePathOrdenProvision || "",
				tieneUso: Boolean(group.tieneUso),
				tieneUsoActivo: Boolean(group.tieneUsoActivo),
				idSede: group.idSede ? String(group.idSede) : "",
				idVoucherCliente: group.idVoucherCliente ? String(group.idVoucherCliente) : "",
				clienteFacturacion: group.clienteFacturacion || null,
			};
			const nextVouchers = (group.vouchers || []).map((voucher) => mapPersistedVoucherToStage(voucher));
			setGroupForm(nextGroupForm);
			setStagedVouchers(nextVouchers);
			setGroupDraftBaseline(buildGroupDraftFingerprint(nextGroupForm, nextVouchers));
		} catch (error) {
			setOpenGroupModal(false);
			clearGroupState();
			handleSnackbar(error?.message || "No se pudo abrir el grupo", "error");
		}
	};

	const handleVoucherSubmit = (submissionItems) => {
		if (!canEditGroups) return;
		if (!submissionItems?.length) return;
		setStagedVouchers((prev) => {
			const replacements = new Map(
				submissionItems
					.filter((item) => item?.replaceTempId)
					.map((item) => [item.replaceTempId, item.voucher])
			);

			let next = prev.map((currentVoucher) => {
				if (!replacements.has(currentVoucher.tempId)) return currentVoucher;
				const nextVoucher = replacements.get(currentVoucher.tempId);
				return {
					...currentVoucher,
					...nextVoucher,
					tempId: currentVoucher.tempId,
					id: currentVoucher.id || nextVoucher.id || null,
					estadoLocal: currentVoucher.id ? "Editado" : "Pendiente",
				};
			});

			const vouchersToAppend = submissionItems
				.filter((item) => !item?.replaceTempId)
				.map((item) => item.voucher);

			if (vouchersToAppend.length) next = [...next, ...vouchersToAppend];
			return next;
		});
		setOpenVoucherModal(false);
		setEditingConfiguration(null);
		setBulkEditMode(false);
		setSelectedConfigurationKey("");
	};

	const buildMutationInput = (overrideEstado = null) => ({
		id: groupForm.id || null,
		modalidad: groupForm.modalidad,
		nombre: groupForm.nombre.trim(),
		descripcion: groupForm.descripcion.trim() || null,
		nroExpediente: normalizedNroExpediente || null,
		estado: overrideEstado || currentGroupState,
		idSede: Number(groupForm.idSede),
		idVoucherCliente: Number(groupForm.idVoucherCliente || 0) || null,
		vouchers: stagedVouchers.map((voucher) => ({
			id: voucher.id || null,
			idCliente: groupForm.modalidad === "FISICO" ? null : Number(voucher.idCliente),
			desde: voucher.desde,
			hasta: voucher.hasta,
			tipo: voucher.tipo,
			idProducto: voucher.tipo === "PRODUCTO" ? Number(voucher.idProducto) : null,
			idCategoria: voucher.tipo === "CATEGORIA" ? Number(voucher.idCategoria) : null,
			cantidad: Number(voucher.cantidad),
			limiteDiario: voucher.limiteDiario == null ? null : Number(voucher.limiteDiario),
			limitePedido: voucher.limitePedido == null ? null : Number(voucher.limitePedido),
			habilitado: voucher.habilitado !== false,
		})),
	});

	const persistGroup = async (overrideEstado = null) => {
		await crearGrupoConVouchers({
			variables: {
				input: buildMutationInput(overrideEstado),
				ordenProvisionFile: groupForm.ordenProvisionFile || undefined,
			},
		});
	};
	const handleSubmitGroup = async (overrideEstado = null) => {
		if (!canEditGroups) return;
		if (overrideEstado && overrideEstado !== persistedGroupState && !canChangeGroupStatus) return;
		if (!groupSedeValid) {
			handleSnackbar("Debe seleccionar una sede de consumo", "error");
			return;
		}
		if (!canSubmitGroup) return;
		const targetEstado = overrideEstado || currentGroupState;
		if (targetEstado === "Activo" && stagedVouchers.length === 0) {
			handleSnackbar("No se puede activar un grupo sin vouchers asociados", "error");
			return;
		}
		if (isEditingGroup && currentGroupState === "Inactivo" && overrideEstado == null) {
			setOpenInactiveDialog(true);
			return;
		}
		try {
			await persistGroup(overrideEstado);
			handleSnackbar(isEditingGroup ? "Grupo actualizado correctamente" : "Grupo creado correctamente", "success");
			setOpenInactiveDialog(false);
			setOpenGroupModal(false);
			clearGroupState();
			refetchGrupos();
		} catch (error) {
			handleSnackbar(error?.message || "No se pudo guardar el grupo", "error");
		}
	};
	const handleSaveGroupAndClose = async () => {
		setDiscardGroupDialogOpen(false);
		await handleSubmitGroup();
	};

	const openGroupStatusMenu = (event) => {
		if (!canEditGroups || !canChangeGroupStatus) return;
		const canChooseActive = stagedVouchers.length > 0;
		const canChooseInactive = isEditingGroup && ["Activo", "Inactivo"].includes(persistedGroupState);
		menu({
			open: event.currentTarget,
			align: "right",
			options: GROUP_STATUS_OPTIONS.map((estado) => ({
				name: estado,
				disabled:
					estado === "Borrador"
						? isEditingGroup && persistedGroupState !== "Borrador"
						: estado === "Activo"
							? !canChooseActive
							: !canChooseInactive,
				icon: { icon: currentGroupState === estado ? "check" : "radio_button_unchecked", color: currentGroupState === estado ? "success" : "secondary" },
				onClick: () => setGroupForm((prev) => ({ ...prev, estado })),
			})),
		});
	};

	const updateTableGroupStatus = async (group, nextEstado) => {
		if (!canChangeGroupStatus) return;
		if (nextEstado === "Activo" && Number(group.cantidadVouchers || 0) === 0) {
			handleSnackbar("No se puede activar un grupo sin vouchers asociados", "error");
			return;
		}
		try {
			await actualizarEstadoVoucherGrupo({ variables: { idGrupo: Number(group.id), estado: nextEstado } });
			if (Number(groupForm.id || 0) === Number(group.id)) {
				setPersistedGroupState(nextEstado);
				setGroupForm((prev) => ({ ...prev, estado: nextEstado }));
			}
			await refetchGrupos();
			handleSnackbar(`Grupo actualizado a ${nextEstado}`, "success");
		} catch (error) {
			handleSnackbar(error?.message || "No se pudo actualizar el estado", "error");
		}
	};

	const openTableStatusMenu = (event, group) => {
		event.stopPropagation();
		if (!canChangeGroupStatus) return;
		const currentEstado = group.estado || "Borrador";
		const options = [
			{ name: currentEstado, disabled: true, icon: { icon: "check", color: "success" } },
		];

		if (currentEstado === "Borrador") {
			options.push({
				name: "Activo",
				disabled: Number(group.cantidadVouchers || 0) === 0,
				icon: { icon: "play_arrow", color: "success" },
				onClick: () => updateTableGroupStatus(group, "Activo"),
			});
		} else if (currentEstado === "Activo") {
			options.push({
				name: "Inactivo",
				icon: { icon: "pause", color: "warning" },
				onClick: () => updateTableGroupStatus(group, "Inactivo"),
			});
		} else if (currentEstado === "Inactivo") {
			options.push({
				name: "Activo",
				disabled: Number(group.cantidadVouchers || 0) === 0,
				icon: { icon: "play_arrow", color: "success" },
				onClick: () => updateTableGroupStatus(group, "Activo"),
			});
		}

		menu({ open: event.currentTarget, align: "right", options });
	};

	const handleFacturarGrupo = async (group) => {
		if (!canInvoiceGroups) return;
		await openEditGroupModal(group.id);
		setGroupEditorTab("facturacion");
	};

	const openGroupActionsMenu = (event, group) => {
		event.stopPropagation();

		const options = [
			canInvoiceGroups && {
				name: "Facturar grupo",
				icon: { icon: "receipt_long", color: "uproPink" },
				onClick: () => handleFacturarGrupo(group),
			},
			canDeleteGroups && {
				name: group.tieneUso ? "Eliminar grupo (tiene consumos)" : "Eliminar grupo",
				icon: { icon: "delete_forever", color: "error" },
				disabled: loadingEliminarGrupo || Boolean(group.tieneUso),
				onClick: () => setGroupDeleteDialog({ open: true, group, confirmValue: "", motivo: "" }),
			},
		].filter(Boolean);
		if (!options.length) return;

		menu({
			open: event.currentTarget,
			align: "right",
			options,
		});
	};

	const handleConfirmDeleteGroup = async () => {
		if (!canDeleteGroups) return;
		if (!canConfirmGroupDelete || !groupDeleteDialog.group) return;

		try {
			await eliminarVoucherGrupo({
				variables: {
					idGrupo: Number(groupDeleteDialog.group.id),
					confirmacionNombre: groupDeleteDialog.confirmValue,
					deleteMotivo: groupDeleteDialog.motivo.trim(),
				},
			});

			if (Number(groupForm.id || 0) === Number(groupDeleteDialog.group.id)) {
				setOpenGroupModal(false);
				clearGroupState();
			}

			closeGroupDeleteDialog();
			await refetchGrupos();
			handleSnackbar("Grupo eliminado correctamente", "success");
		} catch (error) {
			handleSnackbar(error?.message || "No se pudo eliminar el grupo", "error");
		}
	};

	const handleConfirmDeleteVoucher = async () => {
		if (!canDeleteGroups) return;
		if (!voucherDeleteDialog.voucher || !canConfirmVoucherDelete) return;

		const voucher = voucherDeleteDialog.voucher;
		if (!voucher.id) {
			setStagedVouchers((prev) => prev.filter((item) => item.tempId !== voucher.tempId));
			closeVoucherDeleteDialog();
			handleSnackbar("Voucher quitado correctamente", "success");
			return;
		}

		try {
			await eliminarVoucher({
				variables: {
					idVoucher: Number(voucher.id),
					confirmacionDni: voucherDeleteDialog.confirmValue.trim(),
				},
			});

			setStagedVouchers((prev) => prev.filter((item) => item.tempId !== voucher.tempId));

			closeVoucherDeleteDialog();
			await refetchGrupos();
			handleSnackbar("Voucher eliminado correctamente", "success");
		} catch (error) {
			handleSnackbar(error?.message || "No se pudo eliminar el voucher", "error");
		}
	};

	const handleConfirmBulkDelete = async () => {
		if (!canDeleteGroups) return;
		if (!canConfirmBulkDelete) return;
		const selectedTempIdSet = new Set(selectedVoucherTempIds);
		const persistedIds = selectedVouchers.map((voucher) => Number(voucher.id)).filter(Boolean);

		try {
			if (persistedIds.length > 0) {
				await eliminarVouchers({
					variables: {
						idVouchers: persistedIds,
						confirmacionNombreGrupo: bulkDeleteExpectedGroupName,
					},
				});
			}

			setStagedVouchers((prev) => prev.filter((voucher) => !selectedTempIdSet.has(voucher.tempId)));
			setSelectedVoucherTempIds([]);
			setBulkDeleteMode(false);
			closeBulkDeleteDialog();
			if (persistedIds.length > 0) await refetchGrupos();
			handleSnackbar(`${selectedVouchers.length} voucher${selectedVouchers.length === 1 ? "" : "s"} eliminado${selectedVouchers.length === 1 ? "" : "s"} correctamente`, "success");
		} catch (error) {
			handleSnackbar(error?.message || "No se pudieron eliminar los vouchers", "error");
		}
	};

	const groupOptions = useMemo(
		() => [...grupos]
			.sort((left, right) => String(left?.nombre || "").localeCompare(String(right?.nombre || ""), "es"))
			.map((group) => ({ id: Number(group.id), nombre: group.nombre || `Grupo ${group.id}` })),
		[grupos]
	);
	const expedienteOptions = useMemo(
		() => Array.from(new Set(grupos.map((group) => String(group?.nroExpediente || "").trim()).filter(Boolean)))
			.sort((left, right) => left.localeCompare(right, "es", { numeric: true })),
		[grupos]
	);
	const invalidGroupDateRange = Boolean(
		groupTableFilters.desde
		&& groupTableFilters.hasta
		&& dayjs(groupTableFilters.hasta).isBefore(dayjs(groupTableFilters.desde), "day")
	);
	const filteredGrupos = useMemo(() => {
		if (invalidGroupDateRange) return [];

		return grupos.filter((group) => {
			const groupTipo = String(group?.tipo || "").trim().toUpperCase();
			const isMixedGroup = !groupTipo && Number(group?.cantidadVouchers || 0) > 0;
			const matchesEstado = !groupTableFilters.estado || group?.estado === groupTableFilters.estado;
			const matchesTipo = !groupTableFilters.tipo
				|| (groupTableFilters.tipo === "MIXTO" ? isMixedGroup : groupTipo === groupTableFilters.tipo);
			const matchesExpediente = !groupTableFilters.nroExpediente
				|| String(group?.nroExpediente || "").trim() === groupTableFilters.nroExpediente;
			const matchesGroup = !groupTableFilters.idGrupo || Number(group.id) === Number(groupTableFilters.idGrupo);
			const matchesModalidad = !groupTableFilters.modalidad || (group.modalidad || "PERSONAL") === groupTableFilters.modalidad;

			if (!matchesEstado || !matchesTipo || !matchesExpediente || !matchesGroup || !matchesModalidad) return false;
			if (!groupTableFilters.desde && !groupTableFilters.hasta) return true;
			if (!group?.desde || !group?.hasta) return false;

			const groupDesde = dayjs(group.desde);
			const groupHasta = dayjs(group.hasta);
			if (!groupDesde.isValid() || !groupHasta.isValid()) return false;
			if (groupTableFilters.desde && groupHasta.isBefore(dayjs(groupTableFilters.desde), "day")) return false;
			if (groupTableFilters.hasta && groupDesde.isAfter(dayjs(groupTableFilters.hasta), "day")) return false;
			return true;
		});
	}, [grupos, groupTableFilters, invalidGroupDateRange]);
	const hasMixedGroups = useMemo(
		() => filteredGrupos.some((group) => !String(group?.tipo || "").trim() && Number(group?.cantidadVouchers || 0) > 0),
		[filteredGrupos]
	);
	const totalHeader = hasMixedGroups ? "Total (Monto / Unidades)" : "Total";
	const consumedHeader = hasMixedGroups ? "Consumido (Monto / Unidades)" : "Consumido";
	const groupColumns = useMemo(() => [
		{
			field: "nombre",
			headerName: "Grupo",
			sortable: true,
			width: "23.5%",
			renderCell: (item) => <SoftBox><SoftTypography variant="caption" color="text" fontWeight="medium">{item.nombre}</SoftTypography><SoftTypography variant="caption" color="secondary" display="block">{item.rawGroup?.modalidad === "FISICO" ? "Físico · sin beneficiarios" : "Personal · con beneficiarios"}</SoftTypography></SoftBox>,
		},
		{
			field: "expediente",
			headerName: "Expediente",
			sortable: true,
			width: "8%",
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.expediente}</SoftTypography>,
		},
		{
			field: "estado",
			headerName: "Estado",
			sortable: false,
			align: "center",
			width: "6.5%",
			renderCell: (item) => {
				const statusBadge = getGroupStatusBadgeProps(item.rawGroup?.estado);
				return <SoftBadge onClick={canChangeGroupStatus ? (event) => openTableStatusMenu(event, item.rawGroup) : undefined} variant="gradient" color={statusBadge.color} size="sm" badgeContent={statusBadge.label} container />
			},
		},
		{
			field: "tipo",
			headerName: "Tipo",
			sortable: false,
			width: "6%",
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.tipo}</SoftTypography>,
		},
		{
			field: "sede",
			headerName: "Sede",
			sortable: true,
			width: "8%",
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.sede}</SoftTypography>,
		},
		{
			field: "clienteFacturacion",
			headerName: "Cliente facturable",
			sortable: false,
			width: "12%",
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.clienteFacturacion}</SoftTypography>,
		},
		{
			field: "desde",
			headerName: "Desde",
			sortable: true,
			align: "center",
			width: "7.5%",
			customSortValue: (item) => item.desdeValue || "",
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.desde}</SoftTypography>,
		},
		{
			field: "hasta",
			headerName: "Hasta",
			sortable: true,
			align: "center",
			width: "7.5%",
			customSortValue: (item) => item.hastaValue || "",
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.hasta}</SoftTypography>,
		},
		{
			field: "clientes",
			headerName: "Cupones",
			sortable: false,
			align: "right",
			width: "6%",
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.clientes}</SoftTypography>,
		},
		{
			field: "total",
			headerName: "Total",
			sortable: false,
			align: "right",
			width: "12%",
			renderHeader: () => (
				<SoftTypography
					variant="button"
					fontWeight="bold"
					textTransform="uppercase"
					color="dark"
					title={totalHeader}
					sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%", textAlign: "right" }}
				>
					{totalHeader}
				</SoftTypography>
			),
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.total}</SoftTypography>,
		},
		{
			field: "consumido",
			headerName: "Consumido",
			sortable: false,
			align: "right",
			width: "14%",
			renderHeader: () => (
				<SoftTypography
					variant="button"
					fontWeight="bold"
					textTransform="uppercase"
					color="dark"
					title={consumedHeader}
					sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%", textAlign: "right" }}
				>
					{consumedHeader}
				</SoftTypography>
			),
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.consumido}</SoftTypography>,
		},
		{
			field: "adjuntos",
			headerName: "Adjuntos",
			sortable: false,
			align: "center",
			width: "6%",
			renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.adjuntos}</SoftTypography>,
		},
		{
			field: "acciones",
			headerName: "",
			sortable: false,
			align: "center",
			width: "3%",
			renderCell: (item) => (
				<SoftBox component="span" display="flex" alignItems="center" justifyContent="center" onClick={(event) => event.stopPropagation()}>
					{canInvoiceGroups || canDeleteGroups ? (
						<Tooltip title="Acciones">
							<Icon onClick={(event) => openGroupActionsMenu(event, item.rawGroup)}>more_vert</Icon>
						</Tooltip>
					) : null}
				</SoftBox>
			),
		},
	], [canChangeGroupStatus, canDeleteGroups, canInvoiceGroups, consumedHeader, loadingEliminarGrupo, totalHeader]);
	const rows = useMemo(
		() =>
			filteredGrupos.map((group) => {
				const displayTipo = group.tipo ? formatVoucherType(group.tipo) : Number(group.cantidadVouchers || 0) > 0 ? "Mixto" : "-";
				const displayTotal = formatVoucherTotalValue(group, hasMixedGroups);
				const displayConsumido = formatVoucherConsumedValue(group, hasMixedGroups);

				return {
					id: Number(group.id),
					nombre: group.nombre || "-",
					expediente: group.nroExpediente || "-",
					estado: group.estado || "Borrador",
					tipo: displayTipo,
					sede: group?.sede?.nombre || "Sin sede asignada",
					clienteFacturacion: group?.clienteFacturacion?.razonSocial || [group?.clienteFacturacion?.apellido, group?.clienteFacturacion?.nombre].filter(Boolean).join(", ") || "Sin sugerencia",
					desde: formatGroupTableDateCell(group.desde),
					desdeValue: group.desde || "",
					hasta: formatGroupTableDateCell(group.hasta),
					hastaValue: group.hasta || "",
					clientes: group.modalidad === "FISICO" ? `${group.cantidadVouchers || 0} tandas` : String(group.cantidadClientes || 0),
					total: displayTotal,
					consumido: displayConsumido,
					adjuntos: group.filePathOrdenProvision ? "OP" : "-",
					rawGroup: group,
				};
			}),
		[filteredGrupos, hasMixedGroups]
	);
	const sizedGroupColumns = useMemo(() => {
		const headerLabels = {
			total: totalHeader,
			consumido: consumedHeader,
		};

		return groupColumns
			.map((column) => {
				const headerLabel = headerLabels[column.field] ?? column.headerName ?? column.label ?? "";
				const headerWidth = measureGroupTableText(
					String(headerLabel).toUpperCase(),
					'700 10px "Roboto", "Helvetica", "Arial", sans-serif'
				) + (column.sortable === false ? 0 : GROUP_TABLE_SORT_ICON_WIDTH);
				const contentWidth = column.field === "acciones"
					? GROUP_TABLE_ACTION_ICON_WIDTH
					: rows.reduce((largestWidth, row) => {
						const value = row[column.field] ?? "-";
						const measuredWidth = measureGroupTableText(
							value,
							'500 12px "Roboto", "Helvetica", "Arial", sans-serif'
						) + (column.field === "estado" ? GROUP_TABLE_STATUS_BADGE_EXTRA_WIDTH : 0);
						return Math.max(largestWidth, measuredWidth);
					}, 0);
				const contentBasedWidth = Math.ceil(
					Math.max(headerWidth, contentWidth)
					+ GROUP_TABLE_CELL_HORIZONTAL_PADDING
					+ (column.field === "acciones" ? 0 : GROUP_TABLE_TEXT_MEASUREMENT_BUFFER)
				);

				return {
					...column,
					width: `${contentBasedWidth}px`,
					grow: 1,
				};
			});
	}, [consumedHeader, groupColumns, rows, totalHeader]);
	const responsiveGroupColumnFields = useMemo(() => {
		if (!groupTableContainerWidth) {
			return isCompactGroupTable ? COMPACT_VISIBLE_GROUP_COLUMN_FIELDS : DEFAULT_VISIBLE_GROUP_COLUMN_FIELDS;
		}

		const widthByField = new Map(
			sizedGroupColumns.map((column) => [column.field, Number.parseFloat(column.width) || 0])
		);
		const availableWidth = Math.max(0, groupTableContainerWidth - GROUP_TABLE_RESPONSIVE_WIDTH_BUFFER);
		let occupiedWidth = widthByField.get("acciones") || 0;
		const fields = [];

		for (const priorityGroup of GROUP_TABLE_COLUMN_PRIORITY) {
			const priorityGroupWidth = priorityGroup.reduce((total, field) => total + (widthByField.get(field) || 0), 0);
			if (fields.length > 0 && occupiedWidth + priorityGroupWidth > availableWidth) break;
			fields.push(...priorityGroup);
			occupiedWidth += priorityGroupWidth;
		}

		return fields;
	}, [groupTableContainerWidth, isCompactGroupTable, sizedGroupColumns]);

	useEffect(() => {
		const container = groupTableContainerRef.current;
		if (!container || typeof ResizeObserver === "undefined") return undefined;

		const updateWidth = (width) => {
			const nextWidth = Math.floor(width);
			setGroupTableContainerWidth((currentWidth) => currentWidth === nextWidth ? currentWidth : nextWidth);
		};
		const observer = new ResizeObserver((entries) => updateWidth(entries[0]?.contentRect?.width || 0));
		updateWidth(container.getBoundingClientRect().width);
		observer.observe(container);

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (groupColumnsCustomizedRef.current) return;
		setVisibleGroupColumnFields((currentFields) => {
			const hasSameFields = currentFields.length === responsiveGroupColumnFields.length
				&& currentFields.every((field, index) => field === responsiveGroupColumnFields[index]);
			return hasSameFields ? currentFields : responsiveGroupColumnFields;
		});
	}, [responsiveGroupColumnFields]);

	const visibleGroupColumns = useMemo(() => {
		const visibleFields = new Set(visibleGroupColumnFields);
		return sizedGroupColumns.filter((column) => column.field === "acciones" || visibleFields.has(column.field));
	}, [sizedGroupColumns, visibleGroupColumnFields]);

	const discardGroupChanges = () => {
		setOpenGroupModal(false);
		setOpenInactiveDialog(false);
		setDiscardGroupDialogOpen(false);
		clearGroupState();
	};

	const closeGroupModal = () => {
		if (hasUnsavedGroupChanges) {
			setDiscardGroupDialogOpen(true);
			return;
		}
		discardGroupChanges();
	};

	const orderPreviewMode = groupForm.ordenProvisionFile
		? getPreviewMode(groupForm.ordenProvisionFile)
		: groupForm.existingOrdenProvisionPath
			? getPreviewMode({ name: groupForm.existingOrdenProvisionPath, type: /\.pdf$/i.test(groupForm.existingOrdenProvisionPath) ? "application/pdf" : "" })
			: null;
	return (
		<DashboardLayout>
			<DashboardNavbar />
			<SoftBox py={3}>
				<Card>
					<SoftBox p={3} display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={1.5}>
						<SoftBox>
							<SoftTypography variant="h6" fontWeight="bold">
								Beneficios y Vouchers
							</SoftTypography>
						</SoftBox>
						<SoftBox display="flex" gap={1} flexWrap="wrap" width={{ xs: "100%", md: "auto" }}>
							<SoftButton
								variant="outlined"
								color="uproPink"
								size="small"
								circular
								onClick={() => setOpenVouchersAsignadosDialog(true)}
								sx={{ flex: { xs: "1 1 auto", md: "0 0 auto" }, cursor: "pointer" }}
							>
								<Icon sx={{ fontWeight: "bold" }}>people_alt</Icon>
								&nbsp;Clientes con vouchers
							</SoftButton>
							{canEditGroups ? (
								<SoftButton color="uproPink" size="small" circular onClick={openCreateGroupModal} sx={{ flex: { xs: "1 1 auto", md: "0 0 auto" }, cursor: "pointer" }}>
									<Icon sx={{ fontWeight: "bold" }}>add</Icon>
									&nbsp;Crear grupo
								</SoftButton>
							) : null}
						</SoftBox>
					</SoftBox>

					<Card
						sx={{
							mx: { xs: 1.5, sm: 3 },
							mb: 2,
							boxShadow: "0 4px 18px rgba(37, 52, 81, 0.05)",
							border: "1px solid #e4e9f1",
							borderRadius: "12px",
							overflow: "hidden",
						}}
					>
						<SoftBox
							sx={{
								display: "flex",
								alignItems: "center",
								minHeight: 52,
								px: { xs: 1.5, sm: 2 },
								borderBottom: groupTableFiltersExpanded ? "1px solid #e8edf4" : "1px solid transparent",
								transition: "border-color 200ms ease",
							}}
						>
							<SoftBox
								component="button"
								type="button"
								onClick={() => setGroupTableFiltersExpanded((expanded) => !expanded)}
								aria-expanded={groupTableFiltersExpanded}
								aria-controls="voucher-group-filters-content"
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									alignSelf: "stretch",
									flex: 1,
									p: 0,
									border: 0,
									background: "transparent",
									color: "#253451",
									textAlign: "left",
									cursor: "pointer",
									"&:focus-visible": {
										outline: "2px solid #635bff",
										outlineOffset: "3px",
										borderRadius: "6px",
									},
								}}
							>
								<Icon sx={{ color: "#635bff", fontSize: "1.15rem !important" }}>filter_alt</Icon>
								<SoftTypography variant="button" fontWeight="bold" color="inherit">Filtros</SoftTypography>
							</SoftBox>
							<SoftButton
								color="uproPink"
								variant="text"
								size="small"
								onClick={() => setGroupTableFilters(DEFAULT_GROUP_TABLE_FILTERS)}
								sx={{
									minWidth: "auto",
									px: 1,
									mr: 0.5,
									fontSize: "0.72rem",
									"&:focus-visible": { outline: "2px solid #635bff", outlineOffset: "2px" },
								}}
							>
								<Icon sx={{ fontSize: "1rem !important" }}>delete_outline</Icon>&nbsp;Limpiar
							</SoftButton>
							<Tooltip title={groupTableFiltersExpanded ? "Ocultar filtros" : "Mostrar filtros"} placement="top">
								<SoftBox
									component="button"
									type="button"
									onClick={() => setGroupTableFiltersExpanded((expanded) => !expanded)}
									aria-label={groupTableFiltersExpanded ? "Ocultar filtros" : "Mostrar filtros"}
									aria-expanded={groupTableFiltersExpanded}
									aria-controls="voucher-group-filters-content"
									sx={{
										display: "grid",
										placeItems: "center",
										width: 32,
										height: 32,
										p: 0,
										border: 0,
										borderRadius: "8px",
										background: "transparent",
										color: "#64748b",
										cursor: "pointer",
										transition: "background-color 200ms ease, color 200ms ease",
										"&:hover": { backgroundColor: "#f1f5f9", color: "#253451" },
										"&:focus-visible": { outline: "2px solid #635bff", outlineOffset: "2px" },
									}}
								>
									<Icon
										sx={{
											fontSize: "1.15rem !important",
											transform: groupTableFiltersExpanded ? "rotate(0deg)" : "rotate(180deg)",
											transition: "transform 200ms ease",
											"@media (prefers-reduced-motion: reduce)": { transition: "none" },
										}}
									>
										expand_less
									</Icon>
								</SoftBox>
							</Tooltip>
						</SoftBox>
						<Collapse in={groupTableFiltersExpanded} timeout={200} unmountOnExit>
							<SoftBox id="voucher-group-filters-content" p={{ xs: 1.5, sm: 2 }}>
								<Grid container spacing={1.5} alignItems="flex-start">
									<Grid item xs={12} sm={6} lg={2}>
										<InputLabel htmlFor="voucher-group-modalidad-filter"><SoftTypography variant="caption" fontWeight="bold">Modalidad</SoftTypography></InputLabel>
										<Select id="voucher-group-modalidad-filter" fullWidth size="small" displayEmpty value={groupTableFilters.modalidad} onChange={(event) => setGroupTableFilters((prev) => ({ ...prev, modalidad: event.target.value }))}>
											<MenuItem value="">Todas</MenuItem><MenuItem value="PERSONAL">Personal</MenuItem><MenuItem value="FISICO">Físico</MenuItem>
										</Select>
									</Grid>
									<Grid item xs={12} sm={6} lg={2}>
										<InputLabel htmlFor="voucher-group-status-filter"><SoftTypography variant="caption" fontWeight="bold">Estado</SoftTypography></InputLabel>
										<Select
											id="voucher-group-status-filter"
											fullWidth
											size="small"
											displayEmpty
											value={groupTableFilters.estado}
											onChange={(event) => setGroupTableFilters((prev) => ({ ...prev, estado: event.target.value }))}
										>
											<MenuItem value="">Todos</MenuItem>
											{GROUP_STATUS_OPTIONS.map((estado) => <MenuItem key={estado} value={estado}>{estado}</MenuItem>)}
										</Select>
									</Grid>
									<Grid item xs={12} sm={6} lg={2}>
										<InputLabel htmlFor="voucher-group-type-filter"><SoftTypography variant="caption" fontWeight="bold">Tipo</SoftTypography></InputLabel>
										<Select
											id="voucher-group-type-filter"
											fullWidth
											size="small"
											displayEmpty
											value={groupTableFilters.tipo}
											onChange={(event) => setGroupTableFilters((prev) => ({ ...prev, tipo: event.target.value }))}
										>
											<MenuItem value="">Todos</MenuItem>
											<MenuItem value="MONTO">Monto</MenuItem>
											<MenuItem value="PRODUCTO">Producto</MenuItem>
											<MenuItem value="CATEGORIA">Categoría</MenuItem>
											<MenuItem value="MIXTO">Mixto</MenuItem>
										</Select>
									</Grid>
									<Grid item xs={12} sm={6} lg={2}>
										<InputLabel htmlFor="voucher-group-expediente-filter"><SoftTypography variant="caption" fontWeight="bold">Expediente</SoftTypography></InputLabel>
										<Autocomplete
											id="voucher-group-expediente-filter"
											options={expedienteOptions}
											value={groupTableFilters.nroExpediente || null}
											onChange={(_, value) => setGroupTableFilters((prev) => ({ ...prev, nroExpediente: value || "" }))}
											noOptionsText="Sin expedientes"
											renderInput={(params) => <TextField {...params} size="small" placeholder="Todos" />}
										/>
									</Grid>
									<Grid item xs={12} sm={6} lg={2}>
										<InputLabel htmlFor="voucher-group-name-filter"><SoftTypography variant="caption" fontWeight="bold">Grupo</SoftTypography></InputLabel>
										<Autocomplete
											id="voucher-group-name-filter"
											options={groupOptions}
											value={groupOptions.find((option) => option.id === Number(groupTableFilters.idGrupo)) || null}
											onChange={(_, value) => setGroupTableFilters((prev) => ({ ...prev, idGrupo: value?.id || null }))}
											getOptionLabel={(option) => option?.nombre || ""}
											isOptionEqualToValue={(option, value) => option.id === value.id}
											noOptionsText="Sin grupos"
											renderInput={(params) => <TextField {...params} size="small" placeholder="Todos" />}
										/>
									</Grid>
									<Grid item xs={12} sm={6} lg={2}>
										<InputLabel htmlFor="voucher-group-from-filter"><SoftTypography variant="caption" fontWeight="bold">Desde</SoftTypography></InputLabel>
										<SoftInput
											id="voucher-group-from-filter"
											type="date"
											value={groupTableFilters.desde}
											onChange={(event) => setGroupTableFilters((prev) => ({ ...prev, desde: event.target.value }))}
											error={invalidGroupDateRange}
										/>
									</Grid>
									<Grid item xs={12} sm={6} lg={2}>
										<InputLabel htmlFor="voucher-group-to-filter"><SoftTypography variant="caption" fontWeight="bold">Hasta</SoftTypography></InputLabel>
										<SoftInput
											id="voucher-group-to-filter"
											type="date"
											value={groupTableFilters.hasta}
											onChange={(event) => setGroupTableFilters((prev) => ({ ...prev, hasta: event.target.value }))}
											inputProps={{ min: groupTableFilters.desde || undefined }}
											error={invalidGroupDateRange}
										/>
									</Grid>
									<Grid item xs={12} md={6} lg={4}>
										<InputLabel htmlFor="voucher-group-columns-filter"><SoftTypography variant="caption" fontWeight="bold">Columnas visibles</SoftTypography></InputLabel>
										<Autocomplete
											id="voucher-group-columns-filter"
											multiple
											disableCloseOnSelect
											disableClearable
											limitTags={3}
											options={GROUP_TABLE_COLUMN_OPTIONS}
											value={GROUP_TABLE_COLUMN_OPTIONS.filter((option) => visibleGroupColumnFields.includes(option.field))}
											onChange={(_, value) => {
												if (value.length > 0) {
													groupColumnsCustomizedRef.current = true;
													setVisibleGroupColumnFields(value.map((option) => option.field));
												}
											}}
											getOptionLabel={(option) => option.label}
											isOptionEqualToValue={(option, value) => option.field === value.field}
											getOptionDisabled={(option) => visibleGroupColumnFields.length === 1 && visibleGroupColumnFields.includes(option.field)}
											noOptionsText="Sin columnas"
											renderTags={(value) => <Chip label={`${value.length} columnas visibles`} size="small" />}
											renderOption={(props, option, { selected }) => createElement(
												"li",
												props,
												<Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />,
												option.label
											)}
											renderInput={(params) => <TextField {...params} size="small" placeholder="Seleccione columnas" />}
										/>
									</Grid>
								</Grid>
								{invalidGroupDateRange ? <SoftTypography variant="caption" color="error" display="block" mt={1}>La fecha Hasta no puede ser anterior a Desde.</SoftTypography> : null}
							</SoftBox>
						</Collapse>
					</Card>

					<SoftBox ref={groupTableContainerRef} pb={3}>
						{loadingGrupos ? <Loading /> : rows.length === 0 ? <SinDatos /> : <DataTableCustom
							columns={visibleGroupColumns}
							data={rows}
							showActions={false}
							showFooter={false}
							sortable
							defaultSort={{ field: "nombre", direction: "asc" }}
							autoCalculateWidths={false}
							maxHeight="560px"
							zebraStripes={false}
							rowPaddingY={0.5}
							headerStyles={{
								backgroundColor: "#ffffff",
								borderBottom: "1px solid #e4e8ee",
								boxShadow: "none",
								"& .MuiTypography-root": {
									fontWeight: 700,
									color: "#64748b",
									fontSize: "0.625rem",
									letterSpacing: "0.025em",
									lineHeight: 1.3,
								},
							}}
							customRowStyles={() => ({
								backgroundColor: "#ffffff",
								"&:hover": { backgroundColor: "#f8fafc" },
							})}
							showVerticalBorders={false}
							styleCard={{ border: "none", boxShadow: "none" }}
							onRowClick={(row) => openEditGroupModal(row.id)}
						/>}
					</SoftBox>
				</Card>
			</SoftBox>

			<Modal open={openGroupModal} onClose={closeGroupModal}>
				<Fade in={openGroupModal}>
					{loadingGroupDetail ? (
						<Card
							sx={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
								width: { xs: "90%" },
								height: "92vh",
								maxHeight: "92vh",
								overflow: "hidden",
								p: 0,
								outline: "none",
							}}
						>
							<SoftBox
								display="flex"
								justifyContent="center"
								alignItems="center"
								height="100%"
								sx={{ backgroundColor: getUproColor("uproPink") }}
							>
								<SoftBox
									display="flex"
									justifyContent="center"
									alignItems="center"
									flexDirection="column"
									gap={2}
								>
									<SoftBox component="img" src={logo_alt} alt="UPrO Logo" width="15rem" />
									<SoftTypography variant="h4" align="center" color="white">
										Obteniendo datos del grupo
									</SoftTypography>
								</SoftBox>
							</SoftBox>
						</Card>
					) : (
						<Card sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: "90%" }, height: "92vh", maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden", p: { xs: 2, md: 3 }, outline: "none" }}>
							<SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
								<SoftBox>
									<SoftTypography variant="h4" fontWeight="bold">
										{isEditingGroup ? "Editar grupo" : "Crear grupo"}
									</SoftTypography>
									<SoftTypography variant="caption" color="secondary">
										{isEditingGroup ? "Edite el grupo y administre sus vouchers asociados." : "Cargue la cabecera del grupo y agregue vouchers si corresponde."}
									</SoftTypography>
								</SoftBox>
								<SoftButton color="secondary" size="small" circular iconOnly onClick={closeGroupModal}>
									<Icon>close</Icon>
								</SoftButton>
							</SoftBox>
							<SoftBox sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: { xs: 0, md: 0.75 }, mr: { xs: 0, md: -0.75 }, pb: 1 }}>
								<Card sx={{ p: 2, mb: 2, boxShadow: "none", border: "1px solid rgba(148,163,184,.16)" }}>
									<SoftBox
										component={isEditingGroup ? "button" : "div"}
										type={isEditingGroup ? "button" : undefined}
										display="flex"
										alignItems="center"
										justifyContent="space-between"
										gap={1}
										mb={isEditingGroup && !groupSectionExpanded ? 2 : 3}
										onClick={isEditingGroup ? () => setGroupSectionExpanded((expanded) => !expanded) : undefined}
										aria-expanded={isEditingGroup ? groupSectionExpanded : undefined}
										aria-controls={isEditingGroup ? "voucher-group-main-fields" : undefined}
										sx={{
											width: "100%",
											p: 0,
											border: 0,
											background: "transparent",
											textAlign: "left",
											cursor: isEditingGroup ? "pointer" : "default",
											transition: "background-color 200ms ease",
											"&:focus-visible": {
												outline: "2px solid",
												outlineColor: "info.main",
												outlineOffset: "4px",
												borderRadius: "8px",
											},
										}}
									>
										<SoftBox display="flex" alignItems="center" gap={1}>
											<SoftBox width="1.7rem" height="1.7rem" borderRadius="50%" display="flex" alignItems="center" justifyContent="center" bgColor="info" color="white" fontSize="0.8rem" fontWeight="bold" sx={{ lineHeight: 1, flexShrink: 0 }}>
												<SoftTypography variant="caption" fontWeight="bold" color="white" sx={{ lineHeight: 1 }}>1</SoftTypography>
											</SoftBox>
											<SoftBox display="flex" alignItems="left" justifyContent="center" flexDirection="column" gap={0.25}>
												<SoftTypography variant="button" fontWeight="bold">
													{!isEditingGroup
														? "Crear grupo"
														: groupForm.tieneUso
															? (
																<>
																	Editar grupo{" "}
																	<SoftTypography variant="caption" color="secondary" fontWeight="bold" textTransform="none">
																		(Los datos principales no pueden editarse porque el grupo ya se está utilizando)
																	</SoftTypography>
																</>
															)
															: "Editar grupo"
													}
												</SoftTypography>
												{!isEditingGroup && (
													<SoftTypography variant="caption" color="secondary" display="block">
														Complete los datos base del grupo y adjunte la documentacion si corresponde.
													</SoftTypography>
												)}
											</SoftBox>
										</SoftBox>
										{isEditingGroup ? (
											<Icon
												aria-hidden="true"
												sx={{
													flexShrink: 0,
													transform: groupSectionExpanded ? "rotate(180deg)" : "rotate(0deg)",
													transition: "transform 200ms ease",
												}}
											>
												expand_more
											</Icon>
										) : null}
									</SoftBox>

									<Collapse in={!isEditingGroup || groupSectionExpanded} timeout="auto">
										<Grid id="voucher-group-main-fields" container spacing={2}>
											<Grid item xs={12}>
												<InputLabel htmlFor="voucher-grupo-modalidad" sx={GROUP_FIELD_LABEL_SX}>Modalidad</InputLabel>
												<Select id="voucher-grupo-modalidad" fullWidth value={groupForm.modalidad} disabled={!canEditGroups || loadingCrear || isEditingGroup || stagedVouchers.length > 0} onChange={(event) => setGroupForm((prev) => ({ ...prev, modalidad: event.target.value }))}>
													<MenuItem value="PERSONAL">Personal · requiere clientes beneficiarios</MenuItem>
													<MenuItem value="FISICO">Físico · papeles sin clientes beneficiarios</MenuItem>
												</Select>
												<SoftTypography variant="caption" color="secondary">{groupForm.modalidad === "FISICO" ? "Cada beneficio representa una tanda de papeles. Para usarlos en el punto de venta de eventos, elija la sede Villa Mercedes y active el grupo. El cliente facturable, si se elige, es independiente de quien entrega los papeles." : "Cada voucher se asigna a un cliente."} La modalidad se define al crear el grupo.</SoftTypography>
											</Grid>
											<Grid item xs={12} md={5}>
												<InputLabel htmlFor="voucher-grupo-nombre" sx={GROUP_FIELD_LABEL_SX}>Nombre</InputLabel>
												<SoftBox sx={GROUP_SOFT_INPUT_SX}>
													<SoftInput
														id="voucher-grupo-nombre"
														value={groupForm.nombre}
														disabled={groupFieldsDisabled}
														onChange={(e) => setGroupForm((prev) => ({ ...prev, nombre: e.target.value }))}
														placeholder="Ingrese nombre del grupo"
														icon={{ component: "topic_outlined", direction: "left" }}
													/>
												</SoftBox>
											</Grid>
											<Grid item xs={12} md={4}>
												<InputLabel htmlFor="voucher-grupo-expediente" sx={GROUP_FIELD_LABEL_SX}>Nro. expediente (opcional)</InputLabel>
												<SoftBox sx={GROUP_SOFT_INPUT_SX}>
													<SoftInput
														id="voucher-grupo-expediente"
														error={duplicateExpediente}
														value={groupForm.nroExpediente}
														disabled={groupFieldsDisabled}
														onChange={(e) => setGroupForm((prev) => ({ ...prev, nroExpediente: e.target.value }))}
														placeholder="Ingrese numero de expediente si corresponde"
														icon={{ component: "folder_copy_outline", direction: "left" }}
													/>
												</SoftBox>
											</Grid>
											<Grid item xs={12} md={3}>
												<InputLabel id="voucher-grupo-estado-label" sx={GROUP_FIELD_LABEL_SX}>Estado</InputLabel>
												<SoftButton
													color="white"
													size="small"
													fullWidth
													disabled={groupStatusDisabled}
													onClick={openGroupStatusMenu}
													aria-labelledby="voucher-grupo-estado-label"
													aria-haspopup="menu"
													sx={{
														height: 42,
														minHeight: 42,
														boxSizing: "border-box",
														justifyContent: "space-between",
														border: "1px solid #d5deea",
														borderRadius: "10px",
														boxShadow: "0 4px 12px rgba(37, 52, 81, 0.08)",
														px: 1.75,
														textTransform: "none",
														transition: "border-color 200ms ease, box-shadow 200ms ease",
														"&:hover": { borderColor: "#aebdd1", boxShadow: "0 4px 12px rgba(37, 52, 81, 0.1)" },
														"&:focus-visible": { outline: "3px solid rgba(52, 99, 206, 0.18)", outlineOffset: 1 },
													}}
												>
													<SoftBox display="flex" alignItems="center" gap={1.15}>
														<SoftBox width={9} height={9} borderRadius="50%" sx={{ backgroundColor: GROUP_STATUS_DOT_COLORS[currentGroupState] || GROUP_STATUS_DOT_COLORS.Borrador, flexShrink: 0 }} />
														<SoftTypography variant="button" color="dark" sx={{ fontSize: "0.875rem", fontWeight: 500, textTransform: "none" }}>{currentGroupState}</SoftTypography>
													</SoftBox>
													<Icon sx={{ color: "#253451" }}>expand_more</Icon>
												</SoftButton>
											</Grid>
											<Grid item xs={12} md={4}>
												<InputLabel id="voucher-grupo-sede-label" required sx={GROUP_FIELD_LABEL_SX}>Sede de consumo</InputLabel>
												<Select
													labelId="voucher-grupo-sede-label"
													id="voucher-grupo-sede"
													fullWidth
													displayEmpty
													required
													value={groupForm.idSede}
													disabled={groupFieldsDisabled}
													onChange={(event) => setGroupForm((prev) => ({ ...prev, idSede: event.target.value }))}
													startAdornment={<InputAdornment position="start" sx={GROUP_INPUT_ADORNMENT_SX}><Icon>location_on</Icon></InputAdornment>}
													sx={GROUP_SELECT_SX}
												>
													<MenuItem value="" disabled>Seleccione una sede</MenuItem>
													{sedes.map((sede) => <MenuItem key={sede.id} value={String(sede.id)}>{sede.nombre}</MenuItem>)}
												</Select>
												<SoftTypography variant="caption" display="block" sx={GROUP_HELPER_TEXT_SX}>
													{groupForm.tieneUso ? "La sede no puede modificarse después del primer uso." : "Seleccione la sede donde podrán consumirse los vouchers."}
												</SoftTypography>
											</Grid>
											<Grid item xs={12} md={8}>
												<SoftBox
													display="flex"
													justifyContent="space-between"
													alignItems="center"
													gap={1}
													flexWrap="wrap"
													mb={0.75}
												>
													<InputLabel id="voucher-grupo-cliente-label" sx={{ ...GROUP_FIELD_LABEL_SX, mb: 0 }}>
														Cliente facturable sugerido
													</InputLabel>
													<SoftButton
														variant="text"
														color="uproGreen"
														size="small"
														disabled={groupClientDisabled}
														onClick={() => canManageBillingClients && setOpenVoucherClienteModal(true)}
														aria-label="Agregar cliente facturable"
														sx={{
															display: canManageBillingClients ? "inline-flex" : "none",
															minWidth: "auto",
															minHeight: "auto",
															p: 0,
															fontSize: "0.75rem",
															lineHeight: 1.25,
															color: `${ACCESSIBLE_GREEN} !important`,
															background: "transparent !important",
															boxShadow: "none",
															"& .MuiIcon-root": { mr: 0.25, fontSize: "1rem" },
														}}
													>
														<Icon>add</Icon>Agregar cliente
													</SoftButton>
												</SoftBox>
												<Select
													labelId="voucher-grupo-cliente-label"
													value={groupForm.idVoucherCliente || ""}
													disabled={groupClientDisabled}
													onChange={(event) => {
														const idVoucherCliente = event.target.value;
														const clienteFacturacion = voucherClientes.find(
															(cliente) => Number(cliente.id) === Number(idVoucherCliente)
														) || null;
														setGroupForm((prev) => ({
															...prev,
															idVoucherCliente,
															clienteFacturacion,
														}));
													}}
													fullWidth
													displayEmpty
													startAdornment={<InputAdornment position="start" sx={GROUP_INPUT_ADORNMENT_SX}><Icon>group</Icon></InputAdornment>}
													sx={GROUP_SELECT_SX}
												>
													<MenuItem value="">
														<em>Sin cliente sugerido</em>
													</MenuItem>
													{voucherClientes.map((cliente) => (
														<MenuItem key={cliente.id} value={String(cliente.id)}>
															{cliente?.razonSocial
																|| [cliente?.apellido, cliente?.nombre].filter(Boolean).join(", ")
																|| "Cliente"} · CUIT {cliente?.cuit || "-"}
														</MenuItem>
													))}
												</Select>
												<SoftTypography variant="caption" display="block" sx={GROUP_HELPER_TEXT_SX}>
													Puede reemplazarse al emitir cada factura.
												</SoftTypography>
											</Grid>
											<Grid item xs={12}>
												<InputLabel><SoftTypography variant="caption" fontWeight="bold">Descripcion</SoftTypography></InputLabel>
												<SoftInput multiline rows={4} value={groupForm.descripcion} disabled={groupFieldsDisabled} onChange={(e) => setGroupForm((prev) => ({ ...prev, descripcion: e.target.value }))} placeholder="Escriba una descripcion del grupo" />
											</Grid>
											<Grid item xs={12}>
												<InputLabel><SoftTypography variant="caption" fontWeight="bold">Orden de provision</SoftTypography></InputLabel>
												<Card sx={{ p: 1.5, boxShadow: "none", border: "1px dashed rgba(148,163,184,.3)" }}>
													<SoftBox display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={1.2}>
														<SoftBox display="flex" alignItems="center" gap={1.25}>
															<SoftBox width="2.75rem" height="2.75rem" borderRadius="xl" bgColor="info" variant="gradient" display="flex" alignItems="center" justifyContent="center" sx={{ lineHeight: 1 }}><Icon sx={{ color: "#fff", display: "block", lineHeight: 1 }}>upload_file</Icon></SoftBox>
															<SoftBox>
																<SoftTypography variant="button" fontWeight="medium">{groupForm.ordenProvisionFile?.name || "Seleccionar archivo"}</SoftTypography>
																<SoftTypography variant="caption" color="secondary" display="block">{groupForm.ordenProvisionFile ? `${formatFileSize(groupForm.ordenProvisionFile.size)}${groupForm.existingOrdenProvisionPath ? " - reemplazara el archivo actual" : ""}` : groupForm.existingOrdenProvisionPath ? "Archivo actual cargado" : "PDF, JPG o PNG"}</SoftTypography>
															</SoftBox>
														</SoftBox>
														<SoftBox display="flex" gap={1} flexWrap="wrap">
															{(groupForm.ordenProvisionFile || groupForm.existingOrdenProvisionPath) && orderPreviewMode !== "file" ? <SoftButton color="white" size="small" circular onClick={() => openAttachmentPreview({ title: "Orden de provision", file: groupForm.ordenProvisionFile, existingPath: groupForm.existingOrdenProvisionPath })}><Icon>visibility</Icon>&nbsp;Ver</SoftButton> : null}
															<SoftButton color="white" size="small" circular disabled={groupAttachmentsDisabled} onClick={() => orderFileInputRef.current?.click()}><Icon>attach_file</Icon>&nbsp;Examinar</SoftButton>
															{groupForm.ordenProvisionFile ? <SoftButton color="secondary" size="small" circular onClick={() => { setGroupForm((prev) => ({ ...prev, ordenProvisionFile: null })); if (orderFileInputRef.current) orderFileInputRef.current.value = ""; }}><Icon>delete</Icon>&nbsp;Quitar</SoftButton> : null}
														</SoftBox>
													</SoftBox>
													<input ref={orderFileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: "none" }} onChange={(e) => setGroupForm((prev) => ({ ...prev, ordenProvisionFile: e.target.files?.[0] || null }))} />
												</Card>
											</Grid>
										</Grid>
									</Collapse>
								</Card>

								{showGroupTabs ? (
									<Tabs
										value={groupEditorTab}
										onChange={(_, value) => setGroupEditorTab(value)}
										variant="fullWidth"
										sx={{ mb: 0.5, borderBottom: "1px solid rgba(148,163,184,.2)" }}
									>
										<Tab value="detalles" label="Detalles" />
										<Tab value="log" label="Detalles históricos de uso" />
										<Tab value="facturacion" label="Facturación" />
									</Tabs>
								) : null}

								{(!showGroupTabs || groupEditorTab === "detalles") ? (
									<Card sx={{ p: 2, boxShadow: "none", border: "1px solid rgba(148,163,184,.16)" }}>
										<SoftBox display="flex" justifyContent="space-between" alignItems={{ xs: "stretch", lg: "center" }} flexDirection={{ xs: "column", lg: "row" }} gap={1.5} mb={2}>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<SoftBox width="1.7rem" height="1.7rem" borderRadius="50%" display="flex" alignItems="center" justifyContent="center" bgColor="info" color="white" fontSize="0.8rem" fontWeight="bold" sx={{ lineHeight: 1, flexShrink: 0 }}><SoftTypography variant="caption" fontWeight="bold" color="white" sx={{ lineHeight: 1 }}>2</SoftTypography></SoftBox>
												<SoftBox>
													<SoftTypography variant="button" fontWeight="bold">Beneficios / Vouchers</SoftTypography>
												</SoftBox>
											</SoftBox>
											<SoftBox
												display="flex"
												alignItems="center"
												justifyContent={{ xs: "flex-start", lg: "flex-end" }}
												gap={1}
												flexWrap={{ xs: "wrap", lg: bulkEditMode ? "nowrap" : "wrap" }}
												width={{ xs: "100%", lg: "auto" }}
												sx={{ minWidth: 0 }}
											>
												{bulkEditMode ? (
													<SoftBox
														display="flex"
														alignItems="center"
														gap={0.75}
														width={{ xs: "100%", lg: 480, xl: 560 }}
														sx={{ minWidth: 0, flexShrink: 1 }}
													>
														<Select
															fullWidth
															size="small"
															displayEmpty
															value={selectedConfigurationKey}
															onChange={(event) => selectConfigurationToEdit(event.target.value)}
															inputProps={{ "aria-label": "Seleccionar configuración de vouchers para editar" }}
															renderValue={(value) => value
																? voucherConfigurations.find((item) => item.key === value)?.label || "Seleccione una configuración"
																: "Seleccione una configuración"}
															sx={{ minWidth: 0, "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }}
														>
															<MenuItem value="" disabled>Seleccione una configuración</MenuItem>
															{voucherConfigurations.map((configuration) => (
																<MenuItem key={configuration.key} value={configuration.key} disabled={configuration.tieneUso}>
																	<Tooltip title={configuration.tieneUso ? "No puede editarse porque al menos un voucher ya fue utilizado" : ""} placement="left">
																		<SoftTypography variant="button" color={configuration.tieneUso ? "secondary" : "dark"} sx={{ whiteSpace: "normal" }}>
																			{configuration.label}{configuration.tieneUso ? " — No editable" : ""}
																		</SoftTypography>
																	</Tooltip>
																</MenuItem>
															))}
														</Select>
														<Tooltip title="Cancelar edición múltiple">
															<SoftButton color="white" size="small" circular iconOnly onClick={cancelBulkEdit} aria-label="Cancelar edición múltiple"><Icon>close</Icon></SoftButton>
														</Tooltip>
													</SoftBox>
												) : (
													canEditGroups ? <SoftButton color="uproYellow" size="small" circular disabled={stagedVouchers.length === 0 || bulkDeleteMode} onClick={startBulkEdit}><Icon>edit_note</Icon>&nbsp;Editar</SoftButton> : null
												)}
												{!bulkEditMode && canDeleteGroups ? (
													<SoftButton
														color={"error"}
														size="small"
														circular
														disabled={(!bulkDeleteMode && stagedVouchers.length === 0) || loadingEliminarVouchers}
														onClick={handleBulkDeleteButton}
													>
														<Icon>{bulkDeleteMode && selectedVoucherTempIds.length === 0 ? "close" : "delete_sweep"}</Icon>&nbsp;
														{bulkDeleteMode ? (selectedVoucherTempIds.length > 0 ? `Confirmar (${selectedVoucherTempIds.length})` : "Cancelar") : "Eliminar"}
													</SoftButton>
												) : null}
												{canEditGroups ? <SoftButton color="uproGreen" size="small" circular onClick={() => { cancelBulkEdit(); setOpenVoucherModal(true); }} sx={{ flexShrink: 0, backgroundColor: `${ACCESSIBLE_GREEN} !important` }}><Icon size="small">add</Icon>&nbsp;{!bulkEditMode && "Agregar beneficio/voucher"}</SoftButton> : null}
											</SoftBox>
										</SoftBox>

										<Grid container spacing={1.2} sx={{ mb: 2 }}>
											<Grid item xs={12} md={6}><SoftInput value={voucherTableFilters.search} placeholder={groupForm.modalidad === "FISICO" ? "Buscar producto o categoría" : "Buscar cliente o DNI"} onChange={(e) => { const value = e.target.value; setVoucherTableFilters((prev) => ({ ...prev, search: value })); setVoucherTablePagination((prev) => ({ ...prev, page: 1 })); }} /></Grid>
											<Grid item xs={12} md={3}><Select fullWidth size="small" value={voucherTableFilters.tipo} displayEmpty onChange={(e) => { setVoucherTableFilters((prev) => ({ ...prev, tipo: e.target.value })); setVoucherTablePagination((prev) => ({ ...prev, page: 1 })); }}><MenuItem value="">Todos los tipos</MenuItem><MenuItem value="MONTO">Monto</MenuItem><MenuItem value="PRODUCTO">Producto</MenuItem><MenuItem value="CATEGORIA">Categoría</MenuItem></Select></Grid>
											<Grid item xs={12} md={3}><Select fullWidth size="small" value={voucherTableFilters.habilitado} displayEmpty onChange={(e) => { setVoucherTableFilters((prev) => ({ ...prev, habilitado: e.target.value })); setVoucherTablePagination((prev) => ({ ...prev, page: 1 })); }}><MenuItem value="">Todos los estados</MenuItem><MenuItem value="true">Habilitados</MenuItem><MenuItem value="false">Deshabilitados</MenuItem></Select></Grid>
										</Grid>

										<SoftBox sx={{ maxHeight: { xs: "none", lg: "38vh" }, overflowY: "auto", pr: 0.5, mr: -0.5 }}>
											{visibleVouchers.length === 0 ? (
												<SoftBox sx={{ border: "1px dashed rgba(148,163,184,.3)", borderRadius: "16px", minHeight: "150px", placeItems: "center" }} display="grid" textAlign="center">
													<SoftBox px={2}><SoftBox width="3rem" height="3rem" borderRadius="xl" bgColor="info" variant="gradient" display="flex" alignItems="center" justifyContent="center" mx="auto" my={1} sx={{ lineHeight: 1 }}><Icon sx={{ color: "#fff", display: "block", lineHeight: 1 }}>redeem</Icon></SoftBox><SoftTypography variant="button" fontWeight="bold">Aun no hay beneficios/vouchers agregados</SoftTypography><SoftTypography variant="caption" color="secondary" display="block">Agregue el primer beneficio o voucher para continuar.</SoftTypography></SoftBox>
												</SoftBox>
											) : (
												<SoftBox display="grid" gap={1.25}>
													{visibleVouchers.map((voucher) => {
														const badge = getVoucherBadgeProps(voucher);
														const deleteDisabledReason = getVoucherDeleteDisabledReason(voucher);
														return (
															<Card key={voucher.tempId} sx={{ p: 1.5, boxShadow: "none", border: "1px solid rgba(148,163,184,.16)" }}>
																<SoftBox display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={1.25}>
																	<SoftBox>
																		<SoftBox display="flex" alignItems="center" flexWrap="wrap" columnGap={0.75} rowGap={0.45} mb={0.25}>
																			<SoftTypography variant="button" fontWeight="bold">{groupForm.modalidad === "FISICO" ? "Tanda de vouchers físicos" : getClienteNombreCompleto(voucher.cliente)}</SoftTypography>
																			{groupForm.modalidad !== "FISICO" && renderClienteIdentityChips(voucher.cliente)}
																		</SoftBox>
																		<SoftTypography variant="caption" color="secondary" display="block">{groupForm.modalidad === "FISICO" ? "Sin beneficiario" : `DNI ${voucher?.cliente?.dni || "-"}`} | {formatDateCell(voucher.desde)} al {formatDateCell(voucher.hasta)}</SoftTypography>
																		<SoftTypography variant="caption" color="secondary" display="block">{formatVoucherType(voucher.tipo)} | {voucher.tipo === "PRODUCTO" ? `Producto: ${voucher?.producto?.nombre || "-"}` : voucher.tipo === "CATEGORIA" ? `Categoría: ${voucher?.categoria?.categoria || "-"}` : "Sin restricción de producto"} | {groupForm.modalidad === "FISICO" ? "Presupuesto de la tanda" : "Cantidad"}: {formatVoucherValue(voucher.cantidad, voucher.tipo)} | Límite diario: {voucher.limiteDiario == null ? "Sin tope" : formatVoucherValue(voucher.limiteDiario, voucher.tipo)}{groupForm.modalidad === "FISICO" ? ` | Por papel: ${formatVoucherValue(voucher.limitePedido ?? voucher.cantidad, voucher.tipo)}` : ""}</SoftTypography>
																	</SoftBox>
																	<SoftBox display="flex" alignItems="center" gap={1.1} flexWrap="wrap" justifyContent="flex-end">
																		{bulkDeleteMode ? (
																			<Tooltip title={deleteDisabledReason}>
																				<span><Checkbox checked={selectedVoucherTempIdSet.has(voucher.tempId)} disabled={Boolean(deleteDisabledReason)} onChange={() => toggleVoucherSelection(voucher)} inputProps={{ "aria-label": `Seleccionar voucher de ${getClienteNombreCompleto(voucher.cliente)}` }} /></span>
																			</Tooltip>
																		) : null}
																		{/* <SoftBadge variant="gradient" color={badge.color} size="sm" badgeContent={badge.label} container /> */}
																		<SoftBox display="flex" alignItems="center" gap={0.5}><SoftTypography variant="caption" color="secondary">{voucher.habilitado === false ? "Deshabilitado" : "Habilitado"}</SoftTypography><Switch checked={voucher.habilitado !== false} disabled={!canEditGroups} onChange={(_, checked) => setStagedVouchers((prev) => prev.map((item) => item.tempId === voucher.tempId ? { ...item, habilitado: checked, estadoLocal: item.id ? "Editado" : "Pendiente" } : item))} inputProps={{ "aria-label": `${voucher.habilitado === false ? "Habilitar" : "Deshabilitar"} voucher de ${getClienteNombreCompleto(voucher.cliente)}` }} sx={{ width: 42, height: 24, py: 0, m: 0, "& .MuiSwitch-thumb": { width: 16, height: 16 } }} /></SoftBox>
																	</SoftBox>
																</SoftBox>
															</Card>
														);
													})}
												</SoftBox>
											)}
										</SoftBox>

										<SoftBox mt={2} display="flex" justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={1}>
											<SoftTypography variant="caption" color="secondary">{`${visibleVouchers.length} de ${voucherTablePagination.total}`}</SoftTypography>
											{voucherTablePagination.pages > 1 ? <CompactPagination length={visibleVouchers.length} total={voucherTablePagination.total} pages={voucherTablePagination.pages} page={voucherTablePagination.page} setPage={(page) => setVoucherTablePagination((prev) => ({ ...prev, page }))} noCard /> : null}
										</SoftBox>
									</Card>
								) : null}

								{showGroupTabs && groupEditorTab === "log" ? (
									<Card sx={{ p: 2, boxShadow: "none", border: "1px solid rgba(148,163,184,.16)" }}>
										<SoftTypography variant="button" fontWeight="bold" display="block" mb={1.5}>Detalles históricos de uso</SoftTypography>
										<SoftBox
											display="grid"
											gap={1.5}
											mb={1.5}
											sx={{
												gridTemplateColumns: {
													xs: "minmax(0, 1fr)",
													sm: "repeat(2, minmax(0, 1fr))",
													lg: "repeat(4, minmax(0, 1fr))",
												},
												alignItems: "stretch",
											}}
										>
											{voucherLogMetricCards.map((metric) => (
												<Card
													key={metric.key}
													sx={{
														minWidth: 0,
														minHeight: 124,
														height: "100%",
														p: 1.25,
														display: "flex",
														flexDirection: "column",
														boxSizing: "border-box",
														position: "relative",
														overflow: "hidden",
														boxShadow: "0 4px 14px rgba(37,52,81,.06)",
														border: "1px solid rgba(148,163,184,.22)",
														borderRadius: "13px",
														background: `linear-gradient(135deg, ${metric.color}08 0%, #fff 38%)`,
													}}
												>
													<SoftBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={0.9} minWidth={0} sx={{ minHeight: 34 }}>
														<SoftBox display="flex" alignItems="center" gap={1} minWidth={0}>
															<SoftBox
																sx={{
																	width: 34,
																	height: 34,
																	minWidth: 34,
																	borderRadius: "9px",
																	display: "grid",
																	placeItems: "center",
																	backgroundColor: `${metric.color}10`,
																	border: `1px solid ${metric.color}12`,
																}}
															>
																<Icon aria-hidden="true" sx={{ color: metric.color, fontSize: "1.15rem" }}>{metric.icon}</Icon>
															</SoftBox>
															<SoftBox minWidth={0}>
																<SoftTypography variant="button" fontWeight="bold" display="block" sx={{ color: "#253451", fontSize: "0.76rem", lineHeight: 1.15 }}>
																	{metric.title}
																</SoftTypography>
																<SoftTypography variant="caption" color="secondary" display="block" sx={{ mt: 0.2, fontSize: "0.62rem", lineHeight: 1.2 }}>
																	{metric.detail}
																</SoftTypography>
															</SoftBox>
														</SoftBox>
														{metric.percentage ? (
															<SoftBox px={0.75} borderRadius="8px" display="flex" alignItems="center" sx={{ height: 28, backgroundColor: `${metric.color}10`, flexShrink: 0 }}>
																<SoftTypography variant="caption" fontWeight="bold" sx={{ color: metric.color, fontSize: "0.68rem", lineHeight: 1 }}>
																	{loadingVoucherLogMetrics ? "—" : metric.percentage}
																</SoftTypography>
															</SoftBox>
														) : null}
													</SoftBox>
													<SoftBox
														display="grid"
														mt={2.2}
														pt={1.6}
														flex={1}
														gap={1}
														sx={{
															borderTop: "1px solid rgba(148,163,184,.18)",
															gridTemplateColumns: metric.secondaryLabel ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
														}}
													>
														{metric.secondaryLabel ? (
															<SoftBox minWidth={0} my={-0.5}>
																<SoftBox display="flex" alignItems="center" gap={0.1} sx={{ minHeight: 14 }}>
																	<Icon aria-hidden="true" sx={{ color: metric.secondaryColor, fontSize: "0.75rem" }}>{metric.secondaryIcon}</Icon>
																	<SoftTypography variant="caption" fontWeight="bold" sx={{ color: metric.secondaryColor, fontSize: "0.62rem", lineHeight: 1 }}>{metric.secondaryLabel}</SoftTypography>
																</SoftBox>
																<SoftTypography variant="h6" fontWeight="bold" color="dark" display="block" sx={{ mt: 0.55, fontSize: "0.9rem", lineHeight: 1.1, overflowWrap: "anywhere" }}>
																	{loadingVoucherLogMetrics ? "—" : metric.secondaryValue}
																</SoftTypography>
															</SoftBox>
														) : null}
														<SoftBox minWidth={0} textAlign="right" sx={metric.secondaryLabel ? { borderLeft: "1px solid rgba(148,163,184,.18)", pl: 1 } : {}}>
															<SoftBox display="flex" alignItems="center" justifyContent="flex-end" sx={{ minHeight: 14 }}>
																<SoftTypography variant="caption" color="secondary" sx={{ fontSize: "0.62rem", lineHeight: 1 }}>{metric.valueLabel}</SoftTypography>
															</SoftBox>
															<SoftTypography variant="h6" fontWeight="bold" color="dark" sx={{ mt: 0.55, fontSize: "0.9rem", lineHeight: 1.1, overflowWrap: "anywhere" }}>
																{loadingVoucherLogMetrics ? "—" : metric.value}
															</SoftTypography>
														</SoftBox>
													</SoftBox>
												</Card>
											))}
										</SoftBox>
										<Grid container spacing={1.2} sx={{ mb: 2 }}>
											<Grid item xs={12} lg={4}><SoftInput value={voucherLogFilters.search} placeholder={groupForm.modalidad === "FISICO" ? "Buscar producto o categoría" : "Buscar cliente o DNI"} inputProps={{ "aria-label": "Buscar cliente o DNI" }} onChange={(e) => setVoucherLogFilters((prev) => ({ ...prev, search: e.target.value }))} /></Grid>
											<Grid item xs={12} sm={6} lg={2}><Select fullWidth size="small" displayEmpty value={voucherLogFilters.estadoPedido} onChange={(e) => { setVoucherLogFilters((prev) => ({ ...prev, estadoPedido: e.target.value })); setVoucherLogPage(1); }}><MenuItem value="">Estados</MenuItem>{["ABIERTO", "CERRADO", "RESERVADO", "ANULADO", "EN PREPARACIÓN", "PENDIENTE DE PAGO", "PAGO EN REVISIÓN"].map((estado) => <MenuItem key={estado} value={estado}>{estado}</MenuItem>)}</Select></Grid>
											<Grid item xs={12} sm={6} lg={2}><Select fullWidth size="small" displayEmpty value={voucherLogFilters.tipoVoucher} onChange={(e) => { setVoucherLogFilters((prev) => ({ ...prev, tipoVoucher: e.target.value })); setVoucherLogPage(1); }}><MenuItem value="">Tipos voucher</MenuItem><MenuItem value="MONTO">Monto</MenuItem><MenuItem value="PRODUCTO">Producto</MenuItem><MenuItem value="CATEGORIA">Categoría</MenuItem></Select></Grid>
											<Grid item xs={12} sm={6} lg={2}><Select fullWidth size="small" displayEmpty value={voucherLogFilters.appUsuario} onChange={(e) => { setVoucherLogFilters((prev) => ({ ...prev, appUsuario: e.target.value })); setVoucherLogPage(1); }}><MenuItem value="">Aplicaciones</MenuItem>{APP_USUARIO_FILTER_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></Grid>
											<Grid item xs={12} sm={6} lg={2}><Select fullWidth size="small" displayEmpty value={voucherLogFilters.tipoUsuario} onChange={(e) => { setVoucherLogFilters((prev) => ({ ...prev, tipoUsuario: e.target.value })); setVoucherLogPage(1); }}><MenuItem value="">Tipos usuario</MenuItem>{[...new Set(stagedVouchers.map((voucher) => voucher?.cliente?.tipoUsuario).filter(Boolean))].map((tipo) => <MenuItem key={tipo} value={tipo}>{getTipoUsuarioLabel(tipo)}</MenuItem>)}</Select></Grid>
											<Grid item xs={12} sm={6}><SoftInput type="date" value={voucherLogFilters.fechaDesde} onChange={(e) => { setVoucherLogFilters((prev) => ({ ...prev, fechaDesde: e.target.value })); setVoucherLogPage(1); }} /></Grid>
											<Grid item xs={12} sm={6}><SoftInput type="date" value={voucherLogFilters.fechaHasta} onChange={(e) => { setVoucherLogFilters((prev) => ({ ...prev, fechaHasta: e.target.value })); setVoucherLogPage(1); }} /></Grid>
										</Grid>
										{loadingVoucherLog ? <Loading /> : voucherLogTableRows.length ? (
											<SoftBox sx={{ width: "100%", maxWidth: "100%", overflowX: "auto", pb: 0.75 }}>
												<SoftBox sx={{ minWidth: 1000 }}>
													<TableV2 columns={voucherLogColumns} rows={voucherLogTableRows} />
												</SoftBox>
											</SoftBox>
										) : <SinDatos titulo="No hay usos registrados" mensaje="No se encontraron pedidos para los filtros seleccionados." />}
										{loadingVoucherLog || voucherLogPagination.total > 0 ? (
											<CustomPagination
												loading={loadingVoucherLog}
												length={voucherLogTableRows.length}
												total={voucherLogPagination.total}
												pages={voucherLogPagination.pages}
												page={voucherLogPage}
												setPage={setVoucherLogPage}
												noCard
											/>
										) : null}
									</Card>
								) : null}

								{showGroupTabs && groupEditorTab === "facturacion" ? <VoucherFacturacionTab grupo={groupForm} puedeFacturar={canInvoiceGroups} puedeAdministrarClientes={canManageBillingClients} /> : null}

								<SoftBox mt={2} pt={2} display="flex" justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={1.25} sx={{ borderTop: "1px solid rgba(148,163,184,.18)" }}>
									<SoftBox>
										{!groupHeaderValid ? <SoftTypography variant="caption" color="error">Complete el nombre del grupo.</SoftTypography> : null}
										{groupHeaderValid && !groupSedeValid ? <SoftTypography variant="caption" color="error">Seleccione una sede de consumo.</SoftTypography> : null}
										{groupHeaderValid && groupSedeValid && duplicateExpediente ? <SoftTypography variant="caption" color="error">Ya existe un grupo con el mismo nro. de expediente.</SoftTypography> : null}
										{groupHeaderValid && groupSedeValid && invalidActiveWithoutVouchers ? <SoftTypography variant="caption" color="error">No se puede seleccionar el estado Activo sin vouchers asociados.</SoftTypography> : null}
									</SoftBox>
									<SoftBox display="flex" gap={1.1} flexWrap="wrap"><SoftButton color="secondary" circular onClick={closeGroupModal}><Icon>cancel</Icon>&nbsp;Cerrar</SoftButton>{canEditGroups ? <SoftButton color="primary" circular disabled={!canSubmitGroup} onClick={() => handleSubmitGroup()}>{isEditingGroup ? "Guardar cambios" : "Crear grupo"}&nbsp;<Icon>done</Icon></SoftButton> : null}</SoftBox>
								</SoftBox>
							</SoftBox>
						</Card>
					)}
				</Fade>
			</Modal>

			<VoucherEditor
				open={canEditGroups && openVoucherModal}
				onClose={() => {
					setOpenVoucherModal(false);
					setEditingConfiguration(null);
					setSelectedConfigurationKey("");
				}}
				onSubmit={handleVoucherSubmit}
				categorias={categorias}
				editingConfiguration={editingConfiguration}
			/>

			<VoucherClienteDialog
				open={canManageBillingClients && openVoucherClienteModal}
				onClose={() => setOpenVoucherClienteModal(false)}
				onSaved={async (cliente) => {
					await refetchVoucherClientes();
					setGroupForm((prev) => ({ ...prev, idVoucherCliente: String(cliente.id), clienteFacturacion: cliente }));
				}}
			/>

			<VouchersAsignadosDialog
				open={openVouchersAsignadosDialog}
				onClose={() => setOpenVouchersAsignadosDialog(false)}
			/>

			<UnsavedChangesDialog
				open={discardGroupDialogOpen}
				onClose={() => setDiscardGroupDialogOpen(false)}
				description="El grupo tiene cambios que todavía no fueron confirmados. Puede guardarlos antes de salir o descartarlos."
				dialogId="discard-voucher-group"
				onDiscard={discardGroupChanges}
				onSave={handleSaveGroupAndClose}
				saveDisabled={!canSubmitGroup}
			/>

			<Dialog open={groupDeleteDialog.open} onClose={closeGroupDeleteDialog} maxWidth="sm" fullWidth>
				<DialogContent sx={{ p: 3 }}>
					<SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
						<SoftTypography variant="h5" fontWeight="bold">Eliminar grupo</SoftTypography>
						<SoftButton color="white" circular iconOnly onClick={closeGroupDeleteDialog}><Icon>close</Icon></SoftButton>
					</SoftBox>
					<SoftTypography variant="button" color="secondary" display="block">Se eliminará el grupo y todos sus vouchers, solo si no tienen consumos registrados. Si ya se utilizaron, desactive el grupo para conservar la facturación y el historial.</SoftTypography>
					<SoftTypography variant="caption" color="secondary" display="block" mt={0.5}>Esta accion no se puede deshacer.</SoftTypography>
					<SoftBox mt={2}>
						<InputLabel><SoftTypography variant="caption" fontWeight="bold">Confirmacion</SoftTypography></InputLabel>
						<SoftInput value={groupDeleteDialog.confirmValue} onChange={(e) => setGroupDeleteDialog((prev) => ({ ...prev, confirmValue: e.target.value }))} />
						<SoftTypography variant="caption" color="secondary" display="block" mt={0.75}>Escribe <SoftTypography variant="text" color="text" mt={0.75}>{groupDeleteDialog.group?.nombre || ""}</SoftTypography> para confirmar borrado</SoftTypography>
					</SoftBox>
					<SoftBox mt={2}>
						<InputLabel><SoftTypography variant="caption" fontWeight="bold">Motivo de borrado</SoftTypography></InputLabel>
						<SoftInput multiline rows={4} value={groupDeleteDialog.motivo} onChange={(e) => setGroupDeleteDialog((prev) => ({ ...prev, motivo: e.target.value }))} placeholder="Describe el motivo del borrado" />
					</SoftBox>
					<SoftBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
						<SoftButton color="secondary" circular onClick={closeGroupDeleteDialog}>Cancelar</SoftButton>
						<SoftButton color="error" circular disabled={!canConfirmGroupDelete} onClick={handleConfirmDeleteGroup}>Eliminar grupo</SoftButton>
					</SoftBox>
				</DialogContent>
			</Dialog>

			<Dialog open={voucherDeleteDialog.open} onClose={closeVoucherDeleteDialog} maxWidth="sm" fullWidth>
				<DialogContent sx={{ p: 3 }}>
					<SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
						<SoftTypography variant="h5" fontWeight="bold">Eliminar voucher</SoftTypography>
						<SoftButton color="white" circular iconOnly onClick={closeVoucherDeleteDialog}><Icon>close</Icon></SoftButton>
					</SoftBox>
					<SoftTypography variant="button" color="dark" display="block" mt={1}>{getVoucherDeleteClientLabel(voucherDeleteDialog.voucher?.cliente)}</SoftTypography>
					<SoftBox mt={2}>
						<InputLabel><SoftTypography variant="caption" fontWeight="bold">Confirmacion</SoftTypography></InputLabel>
						<SoftInput value={voucherDeleteDialog.confirmValue} onChange={(e) => setVoucherDeleteDialog((prev) => ({ ...prev, confirmValue: e.target.value }))} />
						<SoftTypography variant="caption" color="secondary" display="block" mt={0.75}>{`Escribe el DNI ${voucherDeleteExpectedDni} para confirmar borrado.`}</SoftTypography>
					</SoftBox>
					<SoftBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
						<SoftButton color="secondary" circular onClick={closeVoucherDeleteDialog}>Cancelar</SoftButton>
						<SoftButton color="error" circular disabled={!canConfirmVoucherDelete} onClick={handleConfirmDeleteVoucher}>Eliminar voucher</SoftButton>
					</SoftBox>
				</DialogContent>
			</Dialog>

			<Dialog open={bulkDeleteDialog.open} onClose={closeBulkDeleteDialog} maxWidth="sm" fullWidth>
				<DialogContent sx={{ p: 3 }}>
					<SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
						<SoftTypography variant="h5" fontWeight="bold">Eliminar varios vouchers</SoftTypography>
						<SoftButton color="white" circular iconOnly onClick={closeBulkDeleteDialog}><Icon>close</Icon></SoftButton>
					</SoftBox>
					<SoftTypography variant="button" color="secondary" display="block">{`Se eliminaran ${selectedVouchers.length} vouchers seleccionados.`}</SoftTypography>
					<SoftTypography variant="caption" color="secondary" display="block" mt={0.5}>Esta accion no se puede deshacer.</SoftTypography>
					<SoftBox mt={2}>
						<InputLabel><SoftTypography variant="caption" fontWeight="bold">Confirmacion</SoftTypography></InputLabel>
						<SoftInput value={bulkDeleteDialog.confirmValue} onChange={(e) => setBulkDeleteDialog((prev) => ({ ...prev, confirmValue: e.target.value }))} />
						<SoftTypography variant="caption" color="secondary" display="block" mt={0.75}>Escribe <SoftTypography variant="text" color="text">{bulkDeleteExpectedGroupName}</SoftTypography> para confirmar el borrado.</SoftTypography>
					</SoftBox>
					<SoftBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
						<SoftButton color="secondary" circular onClick={closeBulkDeleteDialog}>Cancelar</SoftButton>
						<SoftButton color="error" circular disabled={!canConfirmBulkDelete} onClick={handleConfirmBulkDelete}>Eliminar vouchers</SoftButton>
					</SoftBox>
				</DialogContent>
			</Dialog>

			<Dialog open={previewState.open} onClose={closePreviewDialog} fullWidth maxWidth="lg"><DialogContent sx={{ p: 2 }}><SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}><SoftTypography variant="h5" fontWeight="bold">{previewState.title}</SoftTypography><SoftButton color="white" circular iconOnly onClick={closePreviewDialog}><Icon>close</Icon></SoftButton></SoftBox>{previewState.mode === "image" ? <SoftBox component="img" src={previewState.url} alt={previewState.title} width="100%" sx={{ borderRadius: "12px" }} /> : previewState.mode === "pdf" ? <SoftBox component="iframe" src={previewState.url} title={previewState.title} width="100%" height="680px" sx={{ border: 0, borderRadius: "12px" }} /> : <SoftTypography variant="button">El archivo seleccionado no admite preview en linea.</SoftTypography>}</DialogContent></Dialog>

			<Dialog open={openInactiveDialog} onClose={() => setOpenInactiveDialog(false)} maxWidth="sm" fullWidth><DialogContent sx={{ p: 3 }}><SoftTypography variant="h5" fontWeight="bold" mb={1}>El grupo esta inactivo y los cambios no se aplicaran. Deseas cambiar el estado a Activo?</SoftTypography><SoftBox mt={2} display="flex" justifyContent="flex-end" gap={1}><SoftButton color="secondary" circular onClick={() => handleSubmitGroup("Inactivo")}>No, continuar inactivo</SoftButton><SoftButton color="primary" circular onClick={() => handleSubmitGroup("Activo")}>Activar</SoftButton></SoftBox></DialogContent></Dialog>

			<Footer />
		</DashboardLayout>
	);
}

export default BeneficiosVouchers;
